/**
 * The real item's model for ItemDetailThirdPassView.
 *
 * `useItemDetailData` reads what the item page shows from the stores and
 * services that already own it (no new reads of tables someone else owns);
 * `buildItemDetailModel` shapes it into the view's model and attaches the
 * editors. Nothing here is sample data: where the item has no value the model
 * says so in the view's own empty styling ("Not set.", "No … yet").
 */
import { ValuesTab } from '@/components/prioritization/item-view/activity/ValuesTab';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '@/i18n/formatters';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useItemClassificationStore } from '@/stores/prioritization/itemClassificationStore';
import { useTriageStore } from '@/stores/prioritization/triageStore';
import { useValueProposalStore } from '@/stores/prioritization/valueProposalStore';
import { useAttributesStore } from '@/stores/prioritization/attributesStore';
import { withoutObjectiveAttributes } from '@/lib/objectiveCriteria';
import { fieldsInSpot, formatCustomFieldValue, isSwitchedOffCustomField } from '@/lib/customFieldPlacement';
import { CustomFieldInput } from '@/components/prioritization/item-view/CustomFieldsSection';
import { useItemSourcesStore } from '@/stores/prioritization/itemSourcesStore';
import { itemSourceLabel } from '@/lib/itemSourcesService';
import { useItemExternalLinks, externalLinkDisplayKey } from '@/hooks/prioritization/useItemExternalLinks';
import { useLinearAutoSync } from '@/hooks/prioritization/useLinearAutoSync';
import { useItemFieldSources } from '@/hooks/prioritization/useItemFieldSources';
import { SYNCED_FIELD_LABELS, keptElsewhere, sourceWords, type ItemFieldSources, type SyncedItemField } from '@/lib/itemFieldSources';
import { syncedFrom, writtenByMapping } from '@/lib/fieldMappingApply';
import { getConnectorDefinition, type ConnectorType } from '@/lib/connectors';
import { useEvidenceForItem } from '@/stores/prioritization/evidenceStore';
import { useItemRelationshipsStore } from '@/stores/prioritization/itemRelationshipsStore';
import { useCollectionsStore } from '@/stores/prioritization/collectionsStore';
import { normalizeSequencePair } from '@/lib/sequenceRelationships';
import { getRelationshipGuideEntry } from '@/lib/relationshipGuide';
import { useCommitmentStore } from '@/stores/prioritization/commitmentStore';
import { useEntityCommentsStore } from '@/stores/prioritization/entityCommentsStore';
import { useWatchesStore } from '@/stores/prioritization/watchesStore';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import { useItemPoolsStore } from '@/stores/prioritization/itemPoolsStore';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { criteriaRowsForTrack, useAttributeGovernanceStore } from '@/stores/prioritization/attributeGovernanceStore';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useEnterpriseIdentityStore } from '@/stores/prioritization/enterpriseIdentityStore';
import { useItemTaxonomyStore } from '@/stores/prioritization/itemTaxonomyStore';
import { usePlanningDefaultsStore } from '@/stores/prioritization/planningDefaultsStore';
import { useActorDisplayMap } from '@/hooks/prioritization/useActorDisplayMap';
import { useSystemUsers } from '@/hooks/useSystemUsers';
import { useWorkTypeCatalog } from '@/hooks/prioritization/useWorkTypeCatalog';
import { fetchItemLockState, fetchItemRankedVersions, fetchItemVersions, type ItemLockState, type ItemRankedVersion, type ItemVersion } from '@/lib/itemVersionsService';
import { fetchItemRankings } from '@/lib/itemRankingsService';
import { cycleStandingChip, elsewhereEvaluations, rankStandingLabel, type ItemRanking } from '@/lib/itemRankings';
import { listsInCycle, type CycleRankMode } from '@/lib/itemCycleStanding';
import { computeItemReadiness } from '@/lib/itemReadiness';
import { SUFFICIENT, readItemStandard, type ItemStandardFill } from '@/lib/itemStandard';
import { deriveItemCertainty, type CertaintyLine } from '@/lib/itemCertainty';
import { useItemCertaintyReadingEnabled } from './hooks/useItemCertaintyReadingEnabled';
import { TypePatternDetail } from './TypePatternDetail';
import { PlanLockedNote, type PlanLock } from './PlanActionBand';
import { setAmendingPlan } from '@/lib/itemCycleContext';
import { describeDrift, type ItemPlanDrift } from '@/lib/itemPlanDriftService';
import { PlanPatternDecision } from '@/components/plan-of-record/PlanPatternDecision';
import { valueStatus, VALUE_AI_MODE_LABEL, VALUE_STATUS_LABEL } from '@/lib/valueStatus';
import { aiCanJudgeValue, classificationMark, valueMarkFor, type ValueMarkNames } from '@/lib/valueMark';
import { getAgentDefinition } from '@/lib/ai/agentDefinitions';
import { useValueAiSettingsStore } from '@/stores/prioritization/valueAiSettingsStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useValueAgents } from '@/hooks/useValueAgents';
import { ValueAiBehind } from '@/components/prioritization/values/ValueAiBehind';
import { useDefinitionPolicyStore } from '@/stores/prioritization/definitionPolicyStore';
import { getLineageForItem, type LineageEventRow } from '@/lib/lineageService';
import { getWorkspaceModules, isModuleEnabled } from '@/lib/workspaceModulesService';
import { resolveQueueLabel } from '@/lib/queueLabels';
import { edgeRoleLabel } from '@/lib/relationshipAuditEvents';
import { carriesPhrase } from '@/lib/carriesRelationships';
import { resolveItemEffortCapacityUnits } from '@/lib/itemToPlanningAdapter';
import { listProductNodes } from '@/lib/hierarchy/productNodesService';
import { useProductHierarchyEnabled } from '@/features/hierarchy/hooks/useProductHierarchyEnabled';
import {
  buildHierarchyTree,
  flattenHierarchyTree,
  formatHierarchyPathLabel,
  productNodeToHierarchyNode,
  type ProductNode,
} from '@/lib/hierarchy/hierarchyTypes';
import { getItemCategoryLabel } from '@/components/prioritization/TrackCellBadge';
import { describeItemEffortSource } from '@/components/prioritization/item-view/ItemDetailEditable';
import { useItemStrategy, type AlignmentStrength, type ItemStrategy } from './useItemStrategy';
import { buildStrategyRows } from './itemStrategyRows';
import { COVERAGE_LABEL } from '@/components/prioritization/item-view/ItemCommitmentsSection';
import { listAssignmentsForItem, assignmentStandingWord, planCoverageWord, assigneeLabel, type AssignmentView } from '@/lib/assignmentService';
import { delegationLevelLabel, listRevisionDelegations, type Delegation, type DelegationLadder } from '@/lib/delegation';
import { useDelegationLadder } from '@/hooks/useDelegationLadder';
import { getEvidenceTypeLabel, type Evidence } from '@/types/prioritization/evidence';
import { altitudeLabelsFor, altitudeNumberOf, formatAltitude, altitudeTagOf } from '@/lib/altitudeDisplay';
import { useItemCategoriesStore } from '@/stores/prioritization/itemCategoriesStore';
import {
  PLANNING_MATURITY_LABELS,
  PLANNING_MATURITY_ORDER,
  KNOWLEDGE_STATUS_LABELS,
  KNOWLEDGE_STATUS_ORDER,
  CONFIDENCE_LEVEL_LABELS,
  CONFIDENCE_LEVEL_ORDER,
  PLANNING_MATURITY_DESCRIPTIONS,
  KNOWLEDGE_STATUS_DESCRIPTIONS,
  CONFIDENCE_LEVEL_DESCRIPTIONS,
} from '@/types/prioritization/itemLifecycle';
import type { Item, ItemFrame } from '@/types/prioritization/lifecycle';
import type { ItemClassificationProposal } from '@/types/prioritization/itemClassification';
import type { Tone } from '@/components/mono/primitives';
import type { AttentionLine, SettingSource } from './itemDetailTypes';
import type { ChoiceOption } from '@/components/shared/ChoiceList';
import {
  InlineChoiceEditor,
  InlineTextEditor,
  type ActionLink,
  type ItemDetailViewModel,
  type LooseEvaluation,
  type MenuEntry,
  type RowEditor,
  type ViewAssessmentRow,
  type ValueAiView,
  type ViewValueAgent,
  type ViewButton,
  type ViewCycleNode,
  type ViewLinkRow,
  type ViewSessionNode,
  type ViewTrackNode,
  type WhatItIsRow,
  type ItemStandardView,
  type PageTarget,
} from './ItemDetailThirdPassView';
import { CategoriesEditor, TriageEditor } from './ItemRowEditors';
import { ObjectRef } from '@/components/ds/ObjectRef';
import { CycleSummary } from '@/components/prioritization/CycleSummary';
import { TrackSummaryCard } from '@/components/prioritization/tracks/TrackSummaryCard';
import { getTrackOwnerUserIdFromMetadata } from '@/types/prioritization/track';
import type { ObjectKind } from '@/components/ds/objectKinds';
import { SessionStepsCard } from '@/features/session-engine-v3/SessionStepsCard';

const SHORT: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
const SHORT_Y: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
const EMPTY: never[] = [];

function day(v: Date | string | null | undefined, withYear = false): string {
  if (!v) return '';
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return formatDate(d, withYear ? SHORT_Y : SHORT);
}

/** A date with no time on it ("2026-12-20"): the day it says, in every time zone. */
function calendarDay(v: string): string {
  return day(`${v.slice(0, 10)}T00:00:00`, true);
}

function iso(v: Date | string | null | undefined): string {
  if (!v) return '';
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/**
 * "How it was created" on Overview, in one sentence, for every item. A person-made
 * item has no badge (canon 30: human is silent), so without this the section was a
 * heading over nothing.
 */
export function itemCreationSentence(
  item: Pick<Item, 'createdAt' | 'creationSource' | 'creationReviewStatus'>,
  creator: string | null,
  sourceLabel: string | null,
): string {
  const on = day(item.createdAt, true);
  const when = `${on ? ` on ${on}` : ''}${sourceLabel ? `, from ${sourceLabel.toLowerCase()}` : ''}`;
  if (item.creationSource === 'ai' || item.creationSource === 'automated') {
    const who = item.creationSource === 'ai' ? 'An agent drafted it' : 'An automated system created it';
    const review = item.creationReviewStatus === 'human_reviewed' ? 'A person has reviewed it.' : 'No person has reviewed it yet.';
    return `${who}${when}. ${review}`;
  }
  return creator ? `${creator} created it${when}.` : `A person created it${when}. Who it was is not recorded.`;
}

function categoryLabel(raw: string | null | undefined): string {
  return getItemCategoryLabel(raw ?? undefined) ?? 'No category';
}

/** The Altitude row's words: the number first, the name as a tag ("3 · Epic"); "Not set" when none. */
function altitudeText(level: number | null | undefined, category: string | null | undefined, labels: ReturnType<typeof altitudeLabelsFor>): string {
  if (level == null || altitudeNumberOf(level) === null) return 'Not set';
  return formatAltitude(level, { category, labels, short: true }) ?? 'Not set';
}

function frameType(f: ItemFrame): string {
  return f.frameType ?? (f as unknown as { frame_type?: string }).frame_type ?? '';
}

const TRIAGE_LABEL: Record<string, string> = {
  pursue: 'Pursue',
  defer: 'Defer',
  reject: 'Reject',
  needs_clarification: 'Needs info',
  redirect: 'Redirected',
};

const EXIT_STATE: Record<string, { label: string; tone: Tone; word: string }> = {
  active: { label: '● Active', tone: 'green', word: 'Active' },
  deferred: { label: '● Deferred', tone: 'mustard', word: 'Deferred' },
  archived: { label: '● Archived', tone: 'neutral', word: 'Archived' },
};

const LINEAGE_WORDS: Record<string, [string, string]> = {
  split: ['Split into', 'Split from'],
  merge: ['Merged into', 'Merged in'],
  fork: ['Forked into', 'Forked from'],
  supersede: ['Superseded by', 'Supersedes'],
  retire: ['Retired', 'Retired'],
};

/** The relationship kinds drawn on Connections, in the order the old inspector listed them (canon 07). */
export type RelationshipKind = 'cluster' | 'package' | 'exclusive' | 'sequence' | 'composition' | 'contains' | 'elaboration' | 'carries' | 'collection';
export const RELATIONSHIP_KINDS: { kind: RelationshipKind; label: string; does: string }[] = [
  { kind: 'cluster', label: 'Cluster', does: 'The same need, worded differently or sent in more than once.' },
  { kind: 'package', label: 'Package', does: 'Done together or not at all.' },
  { kind: 'exclusive', label: 'Competing', does: 'One or the other, not both.' },
  { kind: 'sequence', label: 'Sequence', does: 'What has to come before this, and what is waiting on it.' },
  { kind: 'composition', label: 'Composition', does: 'A whole and its parts.' },
  { kind: 'contains', label: 'Contains', does: 'Where it sits in a reporting hierarchy.' },
  {
    kind: 'elaboration',
    label: getRelationshipGuideEntry('elaboration')?.label ?? 'Elaboration',
    does: 'Other ways of meeting the same need.',
  },
  // Canon 12 §5.11: explains a rank; says nothing about dependency, selection or order.
  { kind: 'carries', label: 'Carries', does: 'Part of the worth of the items it enables, and how much.' },
  { kind: 'collection', label: 'Collection', does: 'Kept together by hand. Carries no weight in ranking.' },
];

/** The lineage acts drawn first on History, in the order the dialog lists them. */
export type LineageActType = 'split' | 'merge' | 'fork' | 'supersede';
export const LINEAGE_ACTS: { type: LineageActType; label: string; does: string }[] = [
  { type: 'split', label: 'Split', does: 'One item becomes two or more new items. This one is retired.' },
  { type: 'merge', label: 'Merge', does: 'This and other items become one new item. The originals are retired.' },
  { type: 'fork', label: 'Fork', does: 'A variant that carries on alongside this one.' },
  { type: 'supersede', label: 'Supersede', does: 'An existing item takes this one’s place. This one is retired.' },
];

const GROUP_LABEL: Record<string, string> = {
  cluster: 'Cluster',
  package: 'Package',
  exclusive: 'Competing',
  related_group: 'Related group',
  collection: 'Collection',
};

// ── Data ─────────────────────────────────────────────────────────────────────

export interface ItemDetailData {
  versions: ItemVersion[] | null;
  rankedVersions: ItemRankedVersion[];
  lock: ItemLockState | null;
  rankings: ItemRanking[];
  rankingsUnread: string[];
  lineage: LineageEventRow[];
  commitmentsEnabled: boolean;
  /** Assignments whose subject is this item (959): who is bound to do what, and whether it is binding. */
  assignments: AssignmentView[];
  /** How much is delegated on each assignment's revision (1036), by revision id. */
  assignmentLevels: Map<string, Delegation>;
  delegationLadder: DelegationLadder;
  /** What strategy the item serves. Null while loading, and when the workspace has Strategy off. */
  strategy: ItemStrategy | null;
}

/** Async reads that belong to this one item. Store-backed data is read in the page. */
export function useItemDetailData(itemId: string, item: Item | null | undefined): ItemDetailData {
  const [versions, setVersions] = useState<ItemVersion[] | null>(null);
  const [rankedVersions, setRankedVersions] = useState<ItemRankedVersion[]>([]);
  const [lock, setLock] = useState<ItemLockState | null>(null);
  const [rankings, setRankings] = useState<ItemRanking[]>([]);
  const [rankingsUnread, setRankingsUnread] = useState<string[]>([]);
  const [lineage, setLineage] = useState<LineageEventRow[]>([]);
  const [commitmentsEnabled, setCommitmentsEnabled] = useState(false);
  const [assignments, setAssignments] = useState<AssignmentView[]>([]);
  const [assignmentLevels, setAssignmentLevels] = useState<Map<string, Delegation>>(new Map());
  const [strategyEnabled, setStrategyEnabled] = useState(false);
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const strategy = useItemStrategy(itemId, workspaceId, strategyEnabled);

  // What the item says changes its version; re-read after a save.
  const saysKey = `${item?.title ?? ''}|${item?.description ?? ''}|${item?.itemCategory ?? ''}|${item?.altitudeLevel ?? ''}|${item?.frames?.length ?? 0}|${item?.effortCapacityUnits ?? ''}|${item?.work_type ?? ''}|${item?.launch_type ?? ''}|${item?.investment_type ?? ''}`;
  useEffect(() => {
    let live = true;
    Promise.all([
      fetchItemVersions(itemId).catch(() => null),
      fetchItemLockState(itemId).catch(() => null),
      fetchItemRankedVersions(itemId).catch(() => [] as ItemRankedVersion[]),
    ]).then(([v, l, rv]) => {
      if (!live) return;
      setVersions(v);
      setLock(l);
      setRankedVersions(rv);
    });
    return () => {
      live = false;
    };
  }, [itemId, saysKey]);

  useEffect(() => {
    let live = true;
    fetchItemRankings(itemId)
      .then((r) => {
        if (!live) return;
        setRankings(r.rankings);
        setRankingsUnread(r.unread);
      })
      .catch(() => {
        if (live) setRankingsUnread(['ranks of record', 'priority lists', 'session ranks']);
      });
    getLineageForItem(itemId)
      .then((rows) => live && setLineage(rows))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [itemId]);

  useEffect(() => {
    if (!workspaceId) return;
    let live = true;
    getWorkspaceModules(workspaceId).then(({ data }) => {
      if (!live) return;
      setCommitmentsEnabled(isModuleEnabled(data, 'commitment_registry'));
      setStrategyEnabled(isModuleEnabled(data, 'strategy'));
    });
    return () => {
      live = false;
    };
  }, [workspaceId]);

  useEffect(() => {
    if (!commitmentsEnabled || !itemId) { setAssignments([]); return; }
    let cancelled = false;
    void listAssignmentsForItem(itemId)
      .then(async (rows) => {
        if (cancelled) return;
        setAssignments(rows);
        const levels = await listRevisionDelegations(rows.flatMap((r) => [r.in_force_revision_id, r.proposed_revision_id].filter((x): x is string => !!x)));
        if (!cancelled) setAssignmentLevels(levels);
      })
      .catch(() => { if (!cancelled) setAssignments([]); });
    return () => { cancelled = true; };
  }, [commitmentsEnabled, itemId]);

  const delegationLadder = useDelegationLadder(workspaceId);
  return { versions, rankedVersions, lock, rankings, rankingsUnread, lineage, commitmentsEnabled, assignments, assignmentLevels, delegationLadder, strategy };
}

// ── Model ────────────────────────────────────────────────────────────────────

export interface ItemDetailActions {
  /** updateItem through the store; resolves with the refusal reason, or null. */
  save: (updates: Partial<Item>) => Promise<string | null>;
  /**
   * Set when the page shows the item in Action (it is in a Plan of Record in
   * force): the fields the plan holds open a note saying how they may still be
   * changed (amending the plan, or a cycle still ranking the item) instead of an editor.
   */
  actionPlan?: PlanLock | null;
  /** In Action: what the item says now, when the plan holds an earlier version. An edit starts from it. */
  currentWords?: { title: string; description: string } | null;
  /** In Action: the version the plan holds (migration 1019). */
  heldVersion?: number | null;
  /** The cycle the page was opened from, when the item is in it (roadmap S-37). */
  cycleShown?: { id: string; name: string; mode: CycleRankMode } | null;
  /** After an Adopt / Keep decision: read drift again. */
  onDriftChanged?: () => void;
  /** In Action: what has drifted since the plan was authorized (migration 936); null while loading. */
  drift?: ItemPlanDrift | null;
  transferOwner: (userId: string) => Promise<string | null>;
  openIntegrations: () => void;
  openPrint: () => void;
  openVisibility: () => void;
  copyLink: () => void;
  retire: (() => void) | null;
  /** Opens the lineage dialog on one act. Null when the person may not change the item, or it is retired. */
  lineageAction: ((act: LineageActType) => void) | null;
  takeBack: (() => void) | null;
  /** Admin only: the assembled item, as the server sees it. */
  inspect: (() => void) | null;
  openDiscussion: () => void;
  toggleWatch: () => void;
  askAi: MenuEntry[];
  addEvidence: () => void;
  editEvidence: (ev: Evidence) => void;
  /** Opens Assessment, where values are entered. */
  goToAssessment: () => void;
  /** May this person change the item? The standard draws its links to fill a gap only when they may. */
  mayEdit: boolean;
  /** Links the item to an objective, or a goal under it; resolves with the refusal reason, or null. Null when the person may not change the item. */
  linkObjective: ((objectiveId: string, strength: AlignmentStrength, goalId?: string) => Promise<string | null>) | null;
  /** Removes objective links (all of an objective's, or one goal's). Null when the person may not change the item. */
  unlinkObjective: ((mappingIds: string[]) => void) | null;
  /** Removes one goal link; the objective stays linked. Null when the person may not change the item. */
  unlinkGoal: ((mappingId: string, objectiveRowCount: number) => void) | null;
  /** Opens Strategy → Objectives (where objectives are set). */
  openObjectives: () => void;
  /** Opens the dialog that adds one kind of relationship. Null when the person may not change the item. */
  addRelationship: ((kind: RelationshipKind) => void) | null;
  findLinks: (() => void) | null;
  openJourney: () => void;
  /** Null when the person may not decide the agent's proposal (its review roles; the database checks the same). */
  acceptProposal: (() => void) | null;
  dismissProposal: (() => void) | null;
  proposalBusy: boolean;
  proposalError: string | null;
  proposalDecided: { proposal: ItemClassificationProposal; text: string } | null;
}

/** Everything store-backed the page reads, gathered in one hook so the page stays wiring only. */
export function useItemDetailStores(item: Item) {
  const itemId = item.id;
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const workspaces = useTenantStore((s) => s.workspaces);
  const allItems = useItemsStore((s) => s.items);

  const classification = useItemClassificationStore((s) => s.byItem[itemId]);
  const fetchClassification = useItemClassificationStore((s) => s.fetchForItem);
  useEffect(() => {
    void fetchClassification(itemId);
  }, [fetchClassification, itemId]);

  const assessments = useTriageStore((s) => s.assessments);
  const fetchTriage = useTriageStore((s) => s.fetchFromSupabase);
  useEffect(() => {
    void fetchTriage?.();
  }, [fetchTriage]);

  const acceptedValues = useValueProposalStore((s) => s.acceptedValues);
  const proposals = useValueProposalStore((s) => s.proposals);
  const valueReplacements = useValueProposalStore((s) => s.replacements);
  const valuesSynced = useValueProposalStore((s) => s.syncState.lastSyncAt != null || s.syncState.isLoading);
  const fetchValues = useValueProposalStore((s) => s.fetchFromSupabase);
  useEffect(() => {
    if (!valuesSynced) void fetchValues();
  }, [valuesSynced, fetchValues]);

  // The every-item rules: subscribed so the attention list redraws once they load.
  useDefinitionPolicyStore((s) => s.overrides);

  // How AI helps with each value (migration 920).
  const valueAiSettings = useValueAiSettingsStore((s) => s.settings);
  const valueAgentName = useValueAiSettingsStore((s) => s.agentName);
  useValueAiSettingsStore((s) => s.agents);
  const loadValueAi = useValueAiSettingsStore((s) => s.load);
  useEffect(() => {
    const ws = item.workspace_id ?? workspaceId;
    if (ws) void loadValueAi(ws);
  }, [loadValueAi, item.workspace_id, workspaceId]);
  const mayAdminister = useUserStore((s) => s.can('admin.settings'));
  // The agent on each value, on the closed row (owner 2026-10-21): one copy, shared with the value card.
  const valueAgents = useValueAgents(itemId);
  const mayDecideValues = useUserStore((s) => s.can('values.accept') || s.can('values.override'));

  const attributes = useAttributesStore((s) => s.attributes);
  const attributesSynced = useAttributesStore((s) => s.syncState.lastSyncAt != null || s.syncState.isLoading);
  const fetchAttributes = useAttributesStore((s) => s.fetchFromSupabase);
  useEffect(() => {
    if (!attributesSynced) void fetchAttributes();
  }, [attributesSynced, fetchAttributes]);

  const evidence = useEvidenceForItem(itemId);

  const edges = useItemRelationshipsStore((s) => s.edges);
  const groups = useItemRelationshipsStore((s) => s.groups);
  const memberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const relationshipsSynced = useItemRelationshipsStore((s) => s.syncState.lastSyncAt != null || s.syncState.isLoading);
  const fetchRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  useEffect(() => {
    if (!relationshipsSynced) void fetchRelationships();
  }, [relationshipsSynced, fetchRelationships]);

  const collections = useCollectionsStore((s) => s.collections);
  const collectionMemberships = useCollectionsStore((s) => s.memberships);
  const fetchCollections = useCollectionsStore((s) => s.fetchFromSupabase);
  useEffect(() => {
    void fetchCollections();
  }, [fetchCollections]);

  const commitmentsAll = useCommitmentStore((s) => s.commitments);
  const getLinkedCommitments = useCommitmentStore((s) => s.getLinkedCommitmentsForItem);
  const commitmentsSynced = useCommitmentStore((s) => s.syncState.lastSyncAt != null || s.syncState.isLoading);
  const fetchCommitments = useCommitmentStore((s) => s.fetchFromSupabase);

  const comments = useEntityCommentsStore((s) => s.comments);
  const fetchComments = useEntityCommentsStore((s) => s.fetchForEntity);
  useEffect(() => {
    void fetchComments('item', itemId);
  }, [fetchComments, itemId]);

  const watching = useWatchesStore((s) => s.watchedItemIds.has(itemId));
  const fetchWatches = useWatchesStore((s) => s.fetchFromSupabase);
  useEffect(() => {
    void fetchWatches();
  }, [fetchWatches]);

  const sessions = useSessionStore((s) => s.sessions);
  const itemPools = useItemPoolsStore((s) => s.itemPools);
  const tracks = useTrackStore((s) => s.tracks);
  // Tracks' criteria (Criteria & Scales rows); ItemDetailRealPage loads the ones this page shows.
  const criteriaConfigs = useAttributeGovernanceStore((s) => s.cycleConfigs);
  const cycles = useDegapProcessStore((s) => s.cycles);
  const orgUnits = useEnterpriseIdentityStore((s) => s.orgUnits);
  const fetchOrgUnits = useEnterpriseIdentityStore((s) => s.fetchFromSupabase);
  useEffect(() => {
    if (workspaceId) void fetchOrgUnits({ workspaceId });
  }, [fetchOrgUnits, workspaceId]);

  const launchTypes = useItemTaxonomyStore((s) => s.launch_type.items);
  const investmentTypes = useItemTaxonomyStore((s) => s.investment_type.items);
  const fetchTaxonomy = useItemTaxonomyStore((s) => s.fetch);
  useEffect(() => {
    if (!workspaceId) return;
    void fetchTaxonomy('launch_type', workspaceId);
    void fetchTaxonomy('investment_type', workspaceId);
  }, [fetchTaxonomy, workspaceId]);

  const workTypes = useWorkTypeCatalog(item.workspace_id ?? workspaceId ?? undefined);
  const bandHours = usePlanningDefaultsStore((s) => s.defaults.itemEffortBandHours);
  const { users } = useSystemUsers();

  // Outside systems this item is linked to (Jira, Miro, Linear…).
  const externalLinks = useItemExternalLinks(item.workspace_id ?? workspaceId, item.id);
  // A Linear link not synced in a while is re-read when the page opens.
  useLinearAutoSync(item.workspace_id ?? workspaceId, item.id, externalLinks);
  // Which of its fields are kept in, or copied to, those systems.
  const fieldSources = useItemFieldSources(item.workspace_id ?? workspaceId, externalLinks);

  // The workspace's idea sources (Settings → Idea Sources; migration 926 adds how items arrive).
  const itemSources = useItemSourcesStore((s) => s.sources);
  const fetchItemSources = useItemSourcesStore((s) => s.fetchSources);
  const sourcesWorkspace = item.workspace_id ?? workspaceId;
  useEffect(() => {
    if (sourcesWorkspace) void fetchItemSources(sourcesWorkspace);
  }, [sourcesWorkspace, fetchItemSources]);

  // Product placement (hierarchy type `product`, migration 719): offered only when the workspace has the tree on.
  const productHierarchyEnabled = useProductHierarchyEnabled();
  // How sure we are → what the record shows (src/lib/itemCertainty.ts); off until the owner says it is ready (2026-10-15).
  const certaintyReadingEnabled = useItemCertaintyReadingEnabled();
  const [productNodes, setProductNodes] = useState<ProductNode[]>([]);
  const productWorkspace = item.workspace_id ?? workspaceId;
  useEffect(() => {
    if (!productWorkspace || (!productHierarchyEnabled && !item.productNodeId)) {
      setProductNodes([]);
      return;
    }
    let live = true;
    listProductNodes(productWorkspace)
      .then((nodes) => live && setProductNodes(nodes))
      .catch(() => live && setProductNodes([]));
    return () => {
      live = false;
    };
  }, [productWorkspace, productHierarchyEnabled, item.productNodeId]);
  const productName = item.productNodeId ? productNodes.find((n) => n.id === item.productNodeId)?.name ?? null : null;
  const productOptions = useMemo(() => {
    if (!productHierarchyEnabled) return [];
    const hierarchy = productNodes.map(productNodeToHierarchyNode);
    return flattenHierarchyTree(buildHierarchyTree(hierarchy)).map(({ node }) => ({
      value: node.id,
      label: node.name,
      description: formatHierarchyPathLabel(node.id, hierarchy),
    }));
  }, [productHierarchyEnabled, productNodes]);

  const itemCategories = useItemCategoriesStore((s) => s.categories);

  return {
    workspaceId,
    workspaceName: workspaces.find((w) => w.id === (item.workspace_id ?? workspaceId))?.name ?? null,
    allItems,
    classification,
    assessments,
    acceptedValues,
    proposals,
    valueReplacements,
    valueAiSettings,
    valueAgentName,
    mayAdminister,
    valueAgents,
    mayDecideValues,
    attributes,
    evidence,
    edges,
    groups,
    memberships,
    collections,
    collectionMemberships,
    commitmentsAll,
    getLinkedCommitments,
    commitmentsSynced,
    fetchCommitments,
    comments,
    watching,
    sessions,
    itemPools,
    tracks,
    criteriaConfigs,
    cycles,
    orgUnits,
    launchTypes,
    investmentTypes,
    workTypes,
    bandHours,
    users,
    productName,
    productOptions,
    productHierarchyEnabled,
    certaintyReadingEnabled,
    itemSources,
    externalLinks,
    fieldSources,
    itemCategories,
  };
}

export type ItemDetailStores = ReturnType<typeof useItemDetailStores>;

/** Names for every person the page mentions. */
export function useItemDetailActorNames(item: Item, data: ItemDetailData, st: ItemDetailStores) {
  const ids = useMemo(() => {
    const out: (string | null | undefined)[] = [item.ownerId, item.delegateId, item.createdBy, item.created_by_user_id, item.promotedBy];
    for (const a of Object.values(st.assessments)) if (a.itemId === item.id) out.push(a.triagedBy);
    for (const p of st.classification?.history ?? EMPTY) out.push(p.acceptedByUserId, p.proposedByUserId);
    for (const l of data.lineage) out.push(l.performed_by);
    for (const av of st.acceptedValues) if (av.itemId === item.id) out.push(av.acceptedBy);
    for (const r of data.rankings) out.push(r.by);
    return out.filter((x): x is string => !!x);
  }, [item, st.assessments, st.classification, data.lineage, data.rankings, st.acceptedValues]);
  return useActorDisplayMap(ids);
}

export function buildItemDetailModel(
  item: Item,
  data: ItemDetailData,
  st: ItemDetailStores,
  names: Record<string, string>,
  a: ItemDetailActions,
): ItemDetailViewModel {
  // In Action, fields the plan holds are changed by amending the plan (owner, 2026-10-15), or in a
  // cycle still ranking the item; the plan keeps the change out until it is authorized again (2026-10-30).
  const planLock = a.actionPlan ?? null;
  const inPlan = (label: string, editor: RowEditor): RowEditor =>
    planLock ? (close) => <PlanLockedNote label={label} plan={planLock} editor={editor} close={close} /> : editor;
  const nameOf = (id: string | null | undefined) => {
    if (!id) return null;
    return names[id] ?? st.users.find((u) => u.id === id)?.name ?? null;
  };
  const titleOf = (id: string) => st.allItems.find((i) => i.id === id)?.title ?? 'An item not loaded here';
  const itemLink = (id: string) => (
    <Link to={`/prioritization/items/${id}`} className="hover:underline">
      {titleOf(id)}
    </Link>
  );

  const versions = data.versions ?? [];
  // In Action the page shows the version the plan holds, not the latest.
  const version = a.heldVersion ?? versions[0]?.version ?? null;
  const exit = EXIT_STATE[item.exit_status ?? 'active'] ?? EXIT_STATE.active;
  const ownerName = nameOf(item.ownerId);
  const frames = item.frames ?? [];
  const category = item.itemCategory ?? null;
  const altitude = item.altitudeLevel ?? null;
  // Altitude is a number, 1 to 5; the name that follows it is a tag, and a category may name its own levels.
  const altitudeLabelsOf = (cat: string | null | undefined) => altitudeLabelsFor(st.itemCategories, cat);
  const altitudeIn = (value: unknown, cat: string | null | undefined, short = false) =>
    formatAltitude(value, { category: cat, labels: altitudeLabelsOf(cat), short });

  // ── Rankings ──
  const official = data.rankings.filter((r) => r.standing === 'official');
  const lists = data.rankings.filter((r) => r.standing === 'agreed' || r.standing === 'proposed');
  const bestList = [...lists].sort((x, y) => (x.standing === y.standing ? (x.rank ?? 999) - (y.rank ?? 999) : x.standing === 'agreed' ? -1 : 1))[0];
  const sessionRanks = data.rankings.filter((r) => r.standing === 'session');

  // ── Tracks and pools the item is in ──
  const poolsWithItem = st.itemPools.filter((p) => p.priorityIds?.includes(item.id));
  const trackIdsFromPools = new Set(poolsWithItem.map((p) => p.trackId).filter(Boolean));
  const sessionById = new Map(st.sessions.map((s) => [s.id, s]));
  const trackById = new Map(st.tracks.map((t) => [t.id, t]));
  const cycleName = (id: string | null | undefined, fallback?: string | null) =>
    (id ? st.cycles.find((c) => c.id === id)?.name : null) ?? fallback ?? 'Cycle';

  // ── Standing strip ──
  // With a cycle shown, the strip reads that cycle: its list and its plan. A plan built from
  // another cycle is a fact in the band above, not this cycle's standing (roadmap S-37).
  const cycleShown = a.cycleShown ?? null;
  const officialFirst = cycleShown ? official.find((r) => r.cycleId === cycleShown.id) : official[0];
  const listHere = cycleShown ? listsInCycle(lists, cycleShown.id, (id) => trackById.get(id)?.cycleId) : lists;
  const bestListHere = [...listHere].sort((x, y) => (x.standing === y.standing ? (x.rank ?? 999) - (y.rank ?? 999) : x.standing === 'agreed' ? -1 : 1))[0];
  // Other tracks, and sessions outside any track, that weighed the item.
  const elsewhere = elsewhereEvaluations({
    poolTrackIds: trackIdsFromPools,
    rankedSessions: data.rankedVersions.map((rv) => ({ sessionId: rv.sessionId, sessionName: rv.sessionName, trackId: sessionById.get(rv.sessionId)?.trackId ?? null })),
    priorityTrackId: bestList?.sourceHref?.split('/').pop() ?? null,
    trackName: (id) => trackById.get(id)?.name,
  });
  const standing = [
    bestListHere
      ? {
          label: 'Priority',
          value: `#${bestListHere.rank ?? '—'} in ${bestListHere.sourceName}`,
          note: `${bestListHere.standing === 'agreed' ? 'Agreed' : 'Proposed'}${bestListHere.at ? ` ${day(bestListHere.at)}` : ''}. Not the plan.`,
        }
      : { label: 'Priority', value: 'Not on a priority list', note: cycleShown ? `No agreed or proposed list in ${cycleShown.name} ranks it yet.` : 'No agreed or proposed list ranks it yet.' },
    officialFirst
      ? {
          label: 'Plan',
          value: officialFirst.rank != null ? `#${officialFirst.rank} in ${officialFirst.sourceName}` : `In ${officialFirst.sourceName}`,
          note: `Of record${officialFirst.at ? ` since ${day(officialFirst.at)}` : ''}.${officialFirst.aboveTheLine === false ? ' Below the line.' : ''}`,
        }
      : cycleShown
        ? { label: 'Plan', value: `Not in a plan of ${cycleShown.name}`, note: 'It has no rank of record in this cycle.' }
        : { label: 'Plan', value: 'Not in a plan of record', note: 'It has no rank of record.' },
    {
      label: 'Elsewhere',
      value: elsewhere.length === 0 ? 'No other evaluations open' : `${elsewhere.length} more ${elsewhere.length === 1 ? 'evaluation' : 'evaluations'} open`,
      note: elsewhere.length === 0 ? (data.rankingsUnread.length ? `Could not read ${data.rankingsUnread.join(', ')}.` : 'Nothing else is considering it.') : elsewhere.join(' · '),
    },
  ];

  // ── Needs attention ──
  // What the workspace's every-item rules say is missing (Settings → Readiness →
  // Every item): a Hard Stop is Required, a Warning is Missing. Missing fields
  // are kept apart from description advice, which is not listed here (owner,
  // 2026-10-15).
  const attention: AttentionLine[] = [];
  const rules = computeItemReadiness(item as unknown as Parameters<typeof computeItemReadiness>[0]);
  for (const issue of rules.required) {
    attention.push({ mark: '! Required', tone: 'coral', text: issue.missingText, scope: 'Every item' });
  }
  for (const issue of rules.recommended) {
    attention.push({ mark: '! Missing', tone: 'mustard', text: issue.missingText, scope: 'Every item' });
  }
  const activeProposal = st.classification?.activeProposal ?? null;
  if (activeProposal) {
    const f = activeProposal.classificationFields;
    attention.push({
      mark: '✦ Proposal',
      tone: 'slate',
      text: `${activeProposal.proposedBy === 'agent' ? 'AI suggests' : 'Someone suggests'} ${categoryLabel(f.category)} at ${altitudeIn(f.altitudeLevel, f.category)}. Unreviewed.`,
      scope: 'This item',
    });
  }
  if (item.creationSource === 'ai' && item.creationReviewStatus !== 'human_reviewed') {
    attention.push({ mark: '! Review', tone: 'mustard', text: 'Drafted by an agent and not yet reviewed by a person.', scope: 'This item' });
  }
  const byCycle = new Map<string, ItemRankedVersion[]>();
  for (const rv of data.rankedVersions) {
    const k = rv.cycleId ?? `session:${rv.sessionId}`;
    byCycle.set(k, [...(byCycle.get(k) ?? []), rv]);
  }
  for (const rows of byCycle.values()) {
    const scope = rows[0].cycleName ?? rows[0].sessionName;
    const changed = rows.filter((r) => r.changedSinceRanked && r.rankedVersion != null);
    if (changed.length === 1) {
      const r = changed[0];
      attention.push({ mark: '! Changed', tone: 'mustard', text: `${r.sessionName} ranked version ${r.rankedVersion}; the item is now version ${r.currentVersion ?? version ?? '—'}.`, scope });
    } else if (changed.length > 1) {
      attention.push({ mark: '! Changed', tone: 'mustard', text: `${changed.length} sessions ranked an earlier version; the item is now version ${changed[0].currentVersion ?? version ?? '—'}.`, scope });
    }
    if (rows.some((r) => r.cycleHasMultipleVersions)) {
      attention.push({ mark: '! Differs', tone: 'mustard', text: 'Sessions in this cycle ranked different versions of the item.', scope });
    }
  }

  // In Action, Needs attention lists drift since the plan was authorized,
  // instead of what is missing (owner, 2026-10-15; migration 936).
  if (planLock) {
    attention.length = 0;
    const typeLabel = (key: string, value: string) =>
      key === 'work_type'
        ? st.workTypes.getLabel(value) ?? value
        : key === 'launch_type'
          ? st.launchTypes.find((t) => t.value === value)?.label ?? value
          : st.investmentTypes.find((t) => t.value === value)?.label ?? value;
    const scope = planLock.name;
    const raw = a.drift?.lines ?? [];
    describeDrift(raw, typeLabel).forEach((words, i) => {
      const l = raw[i];
      attention.push({
        ...words,
        scope,
        // A pattern that moved on: adopt the new version or keep the old (938).
        extra:
          l.kind === 'pattern' ? (
            <PlanPatternDecision
              planCycleId={planLock.planCycleId}
              listKey={l.list_key}
              typeValue={l.type_value}
              typeLabel={typeLabel(l.list_key, l.type_value)}
              wasVersion={l.was_version}
              nowVersion={l.now_version}
              itemId={item.id}
              mayDecide={a.drift?.mayDecide === true}
              onDecided={() => a.onDriftChanged?.()}
            />
          ) : undefined,
      });
    });
    if (a.drift && !a.drift.recorded && a.drift.lines.length === 0) {
      attention.push({ mark: 'Note', tone: 'slate', text: 'This plan was authorized before items’ effort and types were recorded, so only moves, pattern changes and overrides can show here.', scope });
    }
  }

  // ── What it is ──
  const save = a.save;
  const textEditor =
    (label: string, value: string | undefined, field: keyof Item, placeholder: string): RowEditor =>
    (close) => <InlineTextEditor label={label} initial={value ?? ''} multiline placeholder={placeholder} close={close} onSave={(v) => save({ [field]: v || undefined } as Partial<Item>)} />;

  const article = (w: string) => (/^[aeiou]/i.test(w) ? 'an' : 'a');
  const categoriesText: ReactNode = category ? (
    <>
      <span className="block">{categoryLabel(category)}</span>
      {frames.map((f) => {
        const t = frameType(f);
        const word = categoryLabel(t).toLowerCase();
        const wording = f.title && f.title !== item.title ? `: “${f.title}”` : '';
        return (
          <span key={f.id} className="mt-1 block text-[var(--text-sec)]">
            {t === category ? `Also worded as ${article(word)} ${word}` : `Also ${article(word)} ${word}`}
            {f.altitudeLevel && t !== category ? `, ${altitudeIn(f.altitudeLevel, t)}` : ''}
            {wording}
          </span>
        );
      })}
    </>
  ) : null;

  const triage = Object.values(st.assessments)
    .filter((x) => x.itemId === item.id)
    .sort((x, y) => iso(y.triagedAt).localeCompare(iso(x.triagedAt)))[0];
  const TRIAGE_SENTENCE: Record<string, string> = {
    pursue: 'decided to pursue it',
    defer: 'decided to defer it',
    reject: 'decided to reject it',
    needs_clarification: 'asked for more information before deciding',
  };
  const triageWho = triage ? nameOf(triage.triagedBy) : null;
  const triageDid = triage ? TRIAGE_SENTENCE[triage.outcome] ?? `marked it ${String(triage.outcome).replace(/_/g, ' ')}` : '';
  const triageText = triage
    ? `${triageWho ?? 'Someone'} ${triageDid} on ${day(triage.triagedAt)}.${triage.notes ? ` Note: “${triage.notes}”` : ''}`
    : null;

  // The value mark (SPEC-value-mark-icon): who decided each value, category and altitude.
  const markNames: ValueMarkNames = {
    nameOf,
    agentName: (t) => (t ? getAgentDefinition(t)?.name ?? st.valueAgentName(t) : 'The agent'),
  };
  const classifyAi = a.askAi.some((m) => m.label.startsWith('Suggest a category'));
  const classHistory = st.classification?.history ?? EMPTY;
  const categoryMark = category ? classificationMark(item.id, 'category', classHistory, classifyAi, markNames, (v) => categoryLabel(String(v))) : null;
  const altitudeMark = altitude ? classificationMark(item.id, 'altitude', classHistory, classifyAi, markNames, (v) => altitudeText(Number(v), category, altitudeLabelsOf(category))) : null;

  const whatItIs: WhatItIsRow[] = [
    { id: 'why', label: 'Why it matters', text: item.whyNow?.trim() || null, anchorId: 'item-why', editor: textEditor('Why it matters', item.whyNow, 'whyNow', 'Why this, and why now?') },
    {
      id: 'measure',
      label: 'Success measure',
      text: item.evidenceOfSuccess?.trim() || null,
      anchorId: 'item-measure',
      editor: textEditor('Success measure', item.evidenceOfSuccess, 'evidenceOfSuccess', 'How will we know it worked?'),
    },
    {
      id: 'categories',
      label: 'Category',
      text: categoriesText,
      anchorId: 'item-category',
      mark: categoryMark,
      editor: inPlan('Category', (close) => <CategoriesEditor item={item} close={close} save={save} />),
      ai: a.askAi.find((m) => m.label.startsWith('Suggest a category')) ? 'Suggest' : null,
      onAi: a.askAi.find((m) => m.label.startsWith('Suggest a category'))?.onClick,
    },
    {
      id: 'altitude',
      label: 'Altitude',
      text: altitude ? altitudeIn(altitude, category, true) : null,
      mark: altitudeMark,
      editor: inPlan('Altitude', (close) => <CategoriesEditor item={item} close={close} save={save} />),
    },
    { id: 'triage', label: 'Triage', text: triageText, editor: (close) => <TriageEditor itemId={item.id} close={close} /> },
  ];

  // ── AI proposal card ──
  const shown = a.proposalDecided?.proposal ?? activeProposal;
  const proposal: ItemDetailViewModel['proposal'] = shown
    ? {
        label: '✦ Recommended · category and altitude',
        note: 'Recommend only — a person decides',
        from: category ? `${categoryLabel(category)} · ${altitude != null ? altitudeIn(altitude, category) : 'no altitude'}` : 'Not classified',
        to: `${categoryLabel(shown.classificationFields.category)} · ${altitudeIn(shown.classificationFields.altitudeLevel, shown.classificationFields.category)}`,
        why: [
          shown.classificationFields.rationale ? `Why: ${shown.classificationFields.rationale}` : 'No reason was recorded with this suggestion.',
          shown.classificationFields.confidence ? `Confidence “${shown.classificationFields.confidence}” is the agent’s own estimate, not a measured accuracy.` : '',
          `Proposed by ${shown.proposedBy === 'agent' ? `agent (${shown.proposedByAgent ?? 'classifier'})` : nameOf(shown.proposedByUserId) ?? 'a person'}, ${day(shown.createdAt)}.`,
        ]
          .filter(Boolean)
          .join(' '),
        decided: !!a.proposalDecided,
        decidedText: a.proposalDecided?.text,
        actions: a.acceptProposal && a.dismissProposal
          ? [
              { label: 'Accept', onClick: a.acceptProposal, disabled: a.proposalBusy },
              { label: 'Dismiss', onClick: a.dismissProposal, disabled: a.proposalBusy },
            ]
          : [],
        footnote: a.acceptProposal
          ? `Accepting sets this item’s category and altitude${version != null ? `, which makes version ${version + 1}` : ''}.`
          : 'Deciding this suggestion needs one of the agent’s review roles (item manager by default) or a workspace administrator.',
        error: a.proposalError,
      }
    : null;

  // ── Assessment ──
  const official_values = st.acceptedValues.filter((v) => v.itemId === item.id && !v.sessionScope);
  // A track's criteria are its Criteria & Scales rows — the list its Criteria tab shows.
  // (Pools carry no criteria; reading pool.criteriaIds made every track say "None chosen yet".)
  const trackCriteria = (trackId: string | null | undefined): string[] => {
    const t = trackId ? st.tracks.find((x) => x.id === trackId) : undefined;
    return t ? [...new Set(criteriaRowsForTrack(st.criteriaConfigs, t).map((c) => c.attributeKey))] : [];
  };
  const criteriaKeys = new Set(poolsWithItem.flatMap((p) => trackCriteria(p.trackId)));
  // Every criterion in the workspace is listed, as the old Values tab did: a value can be set before an evaluation chooses it.
  // A strategic objective (1066) is listed only where the item has a value on it or one of its tracks scores it.
  const attrKeys = [...new Set([...official_values.map((v) => v.attributeKey), ...criteriaKeys, ...withoutObjectiveAttributes(st.attributes).map((x) => x.key)])];
  const evidenceFor = (key: string) => st.evidence.filter((e) => (e.attributeBindings ?? []).some((b) => b.attributeKey === key));
  const attrLabel = (key: string) => st.attributes.find((x) => x.key === key)?.label ?? key;
  // One evidence line, drawn the same in the Evidence list and inside an opened value.
  const evidenceRow = (e: Evidence): ViewLinkRow => {
    const supports = [...new Set((e.attributeBindings ?? []).map((b) => attrLabel(b.attributeKey)))];
    return {
      id: e.id,
      kind: getEvidenceTypeLabel(e.type),
      text: e.summary?.trim() || e.source || 'Untitled evidence',
      meaning: supports.length ? `· supports ${supports.join(', ')}` : e.supportsClaim ? `· ${e.supportsClaim}` : undefined,
      verdict: e.verifiedAt ? 'Verified' : 'Unverified',
      tone: e.verifiedAt ? 'green' : 'neutral',
      actions: [{ label: 'Edit', onClick: () => a.editEvidence(e) }],
    };
  };
  const attributeRows: ViewAssessmentRow[] = attrKeys
    .filter((k) => st.attributes.some((x) => x.key === k) || official_values.some((v) => v.attributeKey === k))
    // A custom field switched off in Settings → Custom Fields is not shown.
    .filter((k) => !isSwitchedOffCustomField(st.attributes.find((x) => x.key === k)))
    .map((key) => {
      const attr = st.attributes.find((x) => x.key === key);
      const av = official_values.find((v) => v.attributeKey === key);
      // A custom field's value is kept on the item, not as an accepted value.
      const fieldValue = attr?.isCustomField
        ? formatCustomFieldValue(attr, (item.metadata as Record<string, unknown> | undefined)?.[key])
        : null;
      const scale = attr?.scales?.find((s) => s.id === (av?.scaleId ?? attr.defaultScaleId)) ?? attr?.scales?.[0];
      // One value per scale: a criterion can be measured on more than one.
      const scales = attr?.scales ?? [];
      const scaleLines = scales.map((sc) => {
        const v = official_values.find((x) => x.attributeKey === key && (x.scaleId ?? null) === (sc.id ?? null));
        const from = syncedFrom(v?.acceptanceNote);
        return {
          value: v && v.value != null && v.value !== '' ? String(v.value) : null,
          scale: sc.label,
          // A value a linked system sent carries that system's logo (owner, 2026-10-16).
          ...(from ? { source: { connectorType: from.system.toLowerCase(), title: `From ${from.system} ${from.record}` } } : {}),
        };
      });
      const perScale = scaleLines.filter((l) => l.value != null).map((l) => `${l.value}${scales.length > 1 ? ` (${l.scale})` : ''}`);
      const ev = evidenceFor(key);
      const usedIn = poolsWithItem.filter((p) => trackCriteria(p.trackId).includes(key)).map((p) => p.name);
      const value = av?.value;
      // Where the value comes from (canon 01 › Field Source, Sync Direction and Conflict Rule):
      // kept in a linked system → its mark, no edit control, "Change it in <system>".
      const keySources = st.fieldSources?.byAttribute[key];
      const keptThere = keptElsewhere(keySources);
      const broughtOver = keptThere && av?.acceptanceNote && writtenByMapping(av.acceptanceNote) && av.acceptanceNote.includes(`at ${keptThere.systemName} sync`);
      const vs = valueStatus(item.id, key, st.acceptedValues, st.proposals, st.valueReplacements);
      const aiSetting = st.valueAiSettings.find((x) => x.attributeKey === key) ?? null;
      const aiMode = aiSetting?.mode ?? 'off';
      const approver = vs.accepted ? nameOf(vs.accepted.acceptedBy) : null;
      const mark = valueMarkFor(vs, aiSetting, aiCanJudgeValue(item.id, key, aiSetting, st.proposals), markNames);
      const ai: ValueAiView = {
        mark,
        modeLabel: VALUE_AI_MODE_LABEL[aiMode],
        agentName: aiSetting?.agentType ? st.valueAgentName(aiSetting.agentType) : null,
        on: aiMode !== 'off',
        statusLabel:
          vs.status === 'none'
            ? null
            : vs.status === 'ai_approved'
              ? `AI · approved${approver ? ` by ${approver}` : ''}`
              : vs.status === 'ai_rule'
                ? `AI · set by rule${approver ? ` (${approver})` : ''}`
                : vs.status === 'ai_overridden'
                  ? `AI · overridden${nameOf(vs.override?.by) ? ` by ${nameOf(vs.override?.by)}` : ''}`
                  : VALUE_STATUS_LABEL[vs.status],
        statusTone: vs.status === 'ai_waiting' ? 'mustard' : vs.status === 'human' || vs.status === 'none' ? 'neutral' : 'slate',
        waitingNote:
          vs.accepted && vs.waiting
            ? `AI recommends ${String(vs.waiting.proposedValue)} · waiting`
            : null,
        behind:
          st.mayAdminister && (item.workspace_id ?? st.workspaceId) ? (
            <ValueAiBehind
              workspaceId={(item.workspace_id ?? st.workspaceId) as string}
              itemId={item.id}
              attributeKey={key}
              setting={aiSetting}
              agentName={st.valueAgentName}
              nameOf={nameOf}
              result={vs}
            />
          ) : undefined,
      };
      const agentState = st.valueAgents.agentFor(key);
      const waitingRec = vs.waiting && !st.valueAgents.hiddenProposers.has(vs.waiting.proposedBy) ? vs.waiting : null;
      const agent: ViewValueAgent | undefined = attr
        ? {
            state:
              agentState.kind === 'ready'
                ? { kind: 'ready', name: agentState.estimator.name, ask: () => agentState.estimator.onEstimate(key) }
                : agentState.kind === 'cannot_run'
                  ? { kind: 'cannot_run', reason: agentState.reason }
                  : agentState.kind === 'needs_key'
                    ? { kind: 'needs_key' }
                    : { kind: 'none' },
            // Only where an estimator could work: a value with a scale or options to choose from.
            turnOnHref: agentState.kind === 'none' && aiMode === 'off' && st.mayAdminister && (scales.length > 0 || (attr.options?.length ?? 0) > 0)
              ? `/prioritization/settings/attributes/${encodeURIComponent(key)}`
              : null,
            waiting: waitingRec
              ? {
                  id: waitingRec.id,
                  agent: waitingRec.proposedByName ?? st.valueAgentName(waitingRec.proposedBy.slice('agent:'.length)),
                  value: String(waitingRec.proposedValue),
                }
              : null,
            mayDecide: !!waitingRec && st.mayDecideValues && st.valueAgents.mayReview(waitingRec.proposedBy),
            accept: st.valueAgents.acceptRecommendation,
            reject: st.valueAgents.rejectRecommendation,
          }
        : undefined;
      return {
        ai,
        agent,
        attribute: attr?.label ?? key,
        attributeKey: attr ? key : undefined,
        accepted: perScale.length ? perScale.join(' · ') : value == null || value === '' ? fieldValue : String(value),
        scale: scales.length ? scales.map((sc) => sc.label).join(' · ') : attr?.type ?? '—',
        scaleLines: scales.length > 0 ? scaleLines : undefined,
        fieldSources: keySources,
        keptThere: keptThere ?? undefined,
        body: attr ? <ValuesTab itemId={item.id} attributeKey={key} canAccept={!keptThere} readOnly={!!keptThere} /> : undefined,
        basis: ev.length > 0 ? `${ev.length} ${ev.length === 1 ? 'source' : 'sources'}` : av ? 'No evidence linked' : fieldValue ? 'Set by hand' : 'Not assessed',
        sources: ev.length > 0 ? ev.map(evidenceRow) : undefined,
        basisTone: ev.length > 0 ? 'green' : av ? 'mustard' : 'neutral',
        usedIn: usedIn.length ? usedIn.join(' · ') : '—',
        details: [
          ...(attr ? [] : [{ label: 'Scale', text: [scale?.label ?? 'No scale recorded'].join('') }]),
          ...(keptThere
            ? [
                {
                  label: 'Where it comes from',
                  text: broughtOver
                    ? `${keptThere.sentence} Last brought over ${av?.acceptedAt ? day(av.acceptedAt) : ''}.`.replace(' .', '.')
                    : av
                      ? `${keptThere.sentence} ${keptThere.systemName} has not sent a value yet; the one shown was set in Priorities.ai.`
                      : keptThere.sentence,
                },
              ]
            : []),
          {
            label: 'Who set it',
            text: av
              ? `Accepted${nameOf(av.acceptedBy) ? ` by ${nameOf(av.acceptedBy)}` : ''}${av.acceptedAt ? `, ${day(av.acceptedAt)}` : ''}${av.acceptanceMethod ? ` (${String(av.acceptanceMethod).replace(/_/g, ' ')})` : ''}.`
              : 'No value has been accepted yet.',
          },
        ],
      };
    });

  const effort = resolveItemEffortCapacityUnits(item, st.bandHours);
  const effortEditor: RowEditor = inPlan('Effort', (close) => (
    <InlineTextEditor
      label="Item effort in capacity units"
      inputType="number"
      initial={item.effortCapacityUnits != null ? String(item.effortCapacityUnits) : ''}
      placeholder="Capacity units (1 CU ≈ 8 hours)"
      close={close}
      onSave={(v) => {
        if (v === '') return save({ effortCapacityUnits: null, effortSource: null });
        const n = Number(v);
        if (!Number.isFinite(n) || n <= 0) return Promise.resolve('Enter a number of capacity units greater than 0.');
        return save({ effortCapacityUnits: n, effortSource: 'explicit' });
      }}
    />
  ));
  const workTypeLabel = item.work_type ? st.workTypes.getLabel(item.work_type) : null;
  const launchLabel = item.launch_type ? st.launchTypes.find((t) => t.value === item.launch_type)?.label ?? item.launch_type : null;
  const investmentLabel = item.investment_type ? st.investmentTypes.find((t) => t.value === item.investment_type)?.label ?? item.investment_type : null;
  const PLAN_FIELDS: (keyof Item)[] = ['work_type', 'launch_type', 'investment_type'];
  const selectEditor = (label: string, initial: string | null | undefined, options: ChoiceOption[], field: keyof Item): RowEditor => {
    const editor: RowEditor = (close) => <InlineChoiceEditor label={label} initial={initial ?? ''} options={options} close={close} onSave={(v) => save({ [field]: v || null } as Partial<Item>)} />;
    return PLAN_FIELDS.includes(field) ? inPlan(label, editor) : editor;
  };
  const workTypeOptions: ChoiceOption[] = st.workTypes.options.map((o) => ({ value: o.value, label: o.label, description: (o as { description?: string | null }).description ?? undefined }));
  const launchOptions: ChoiceOption[] = st.launchTypes.filter((t) => t.isActive).map((t) => ({ value: t.value, label: t.label, description: t.description ?? undefined }));
  const investmentOptions: ChoiceOption[] = st.investmentTypes.filter((t) => t.isActive).map((t) => ({ value: t.value, label: t.label, description: t.description ?? undefined }));

  const assessmentRows: ViewAssessmentRow[] = [
    ...attributeRows,
    {
      attribute: 'Item effort',
      accepted: effort.capacityUnits != null && item.effortCapacityUnits != null ? `${effort.capacityUnits} CU` : null,
      scale: 'capacity units',
      basis: describeItemEffortSource(effort, item.altitudeLevel),
      basisTone: item.effortCapacityUnits != null ? 'neutral' : 'mustard',
      usedIn: 'Planning only',
      note: 'The item’s effort in capacity units (1 CU ≈ 8 hours). It is not an effort score used to compare items, and neither fills in the other.',
      editor: effortEditor,
    },
    { attribute: 'Work type', accepted: workTypeLabel, scale: 'list', basis: item.work_type ? 'Set by hand' : 'Not set', basisTone: item.work_type ? 'neutral' : 'mustard', usedIn: '—', editor: selectEditor('Work type', item.work_type, workTypeOptions, 'work_type') },
    { attribute: 'Launch type', accepted: launchLabel, scale: 'list', basis: item.launch_type ? 'Set by hand' : 'Not set', basisTone: item.launch_type ? 'neutral' : 'mustard', usedIn: '—', editor: selectEditor('Launch type', item.launch_type, launchOptions, 'launch_type') },
  ];

  const evidenceRows: ViewLinkRow[] = st.evidence.map(evidenceRow);

  // ── Connections ──
  const liveEdges = st.edges.filter((e) => (e.sourceItemId === item.id || e.targetItemId === item.id) && e.status !== 'dissolved' && e.status !== 'dismissed');
  const linkRows: ViewLinkRow[] = liveEdges.map((e) => {
    const isSource = e.sourceItemId === item.id;
    const other = isSource ? e.targetItemId : e.sourceItemId;
    return {
      id: e.id,
      kind: `${edgeRoleLabel(e.type, isSource)}${e.subtype ? ` (${String(e.subtype).replace(/_/g, ' ')})` : ''}`,
      text: itemLink(other),
      meaning: e.rationale || e.label || undefined,
      verdict: e.status === 'candidate' ? 'Unreviewed' : undefined,
      tone: 'mustard',
    };
  });
  const myGroupIds = new Set(st.memberships.filter((m) => m.itemId === item.id).map((m) => m.groupId));
  const myGroups = st.groups.filter((g) => myGroupIds.has(g.id) && g.status !== 'dissolved' && g.status !== 'dismissed');
  const groupRow = (g: (typeof myGroups)[number]): ViewLinkRow => {
    const others = st.memberships.filter((m) => m.groupId === g.id && m.itemId !== item.id).map((m) => m.itemId);
    return {
      id: g.id,
      kind: GROUP_LABEL[g.type] ?? g.type,
      text:
        g.type === 'collection' ? (
          g.title ?? 'Untitled collection'
        ) : others.length ? (
          <>
            {others.map((o, i) => (
              <span key={o}>
                {i > 0 ? ', ' : 'With '}
                {itemLink(o)}
              </span>
            ))}
          </>
        ) : (
          'No other items yet.'
        ),
      meaning: g.type === 'collection' ? `· ${others.length} other ${others.length === 1 ? 'item' : 'items'}` : g.title || g.rationale || undefined,
      verdict: g.status === 'candidate' ? 'Unreviewed' : undefined,
      tone: 'mustard',
    };
  };
  // One row per kind: what it means, what is linked, and Add.
  const recordedLine = (key: string, content: ReactNode, unreviewed?: boolean) => (
    <span key={key} className="block text-[var(--text-pri)] [&+&]:mt-1">
      {content}
      {unreviewed ? <span className="text-[13px] text-[var(--mustard)]"> · unreviewed</span> : null}
    </span>
  );
  const edgeLine = (e: (typeof liveEdges)[number], word?: string) => {
    const isSource = e.sourceItemId === item.id;
    const other = isSource ? e.targetItemId : e.sourceItemId;
    return recordedLine(e.id, <>{word ?? edgeRoleLabel(e.type, isSource)} {itemLink(other)}</>, e.status === 'candidate');
  };
  const groupLine = (g: (typeof myGroups)[number]) => {
    const others = st.memberships.filter((mm) => mm.groupId === g.id && mm.itemId !== item.id).map((mm) => mm.itemId);
    return recordedLine(
      g.id,
      others.length ? (
        <>
          {others.map((o, i) => (
            <span key={o}>
              {i > 0 ? ', ' : 'With '}
              {itemLink(o)}
            </span>
          ))}
          {g.title ? ` · ${g.title}` : ''}
        </>
      ) : (
        `${g.title || 'Unnamed'} · no other items yet`
      ),
      g.status === 'candidate',
    );
  };
  const recordedFor = (kind: RelationshipKind): ReactNode[] => {
    if (kind === 'cluster' || kind === 'package' || kind === 'exclusive') return myGroups.filter((g) => g.type === kind).map(groupLine);
    if (kind === 'sequence')
      return liveEdges
        .filter((e) => normalizeSequencePair(e) != null)
        .map((e) => {
          const pair = normalizeSequencePair(e);
          return edgeLine(e, pair?.dependentItemId === item.id ? 'Before this:' : 'Waiting on this:');
        });
    if (kind === 'composition') return liveEdges.filter((e) => e.type === 'aggregation' || e.type === 'breakdown').map((e) => edgeLine(e));
    if (kind === 'contains') return liveEdges.filter((e) => e.type === 'contains').map((e) => edgeLine(e));
    if (kind === 'elaboration') return liveEdges.filter((e) => e.type === 'elaboration').map((e) => edgeLine(e));
    if (kind === 'carries')
      return liveEdges
        .filter((e) => e.type === 'carries')
        .map((e) => {
          // "Carries 40% of <target>" here on the carrier; "<source> carries 40% of this" on the carried item.
          const { before, after } = carriesPhrase(e, item.id);
          const other = e.sourceItemId === item.id ? e.targetItemId : e.sourceItemId;
          return recordedLine(e.id, <>{before}{itemLink(other)}{after}</>, e.status === 'candidate');
        });
    const myCollectionIds = new Set(st.collectionMemberships.filter((cm) => cm.itemId === item.id).map((cm) => cm.collectionId));
    return st.collections
      .filter((c) => myCollectionIds.has(c.id))
      .map((c) =>
        recordedLine(
          c.id,
          <Link to={`/prioritization/collections/${c.id}`} className="hover:underline">
            {c.name || 'Unnamed collection'}
          </Link>,
        ),
      );
  };
  const relationshipRows: ViewLinkRow[] = RELATIONSHIP_KINDS.map((r) => {
    const recorded = recordedFor(r.kind);
    return {
      id: r.kind,
      kind: r.label,
      text: (
        <>
          {recorded.length ? recorded : <span className="block text-[var(--text-dim)]">None</span>}
          <span className="mt-1 block text-[12.5px] leading-snug text-[var(--text-dim)]">{r.does}</span>
        </>
      ),
      actions: a.addRelationship ? [{ label: 'Add', onClick: () => a.addRelationship?.(r.kind) }] : undefined,
    };
  });
  // Links of a kind not listed above (rare) still show, so nothing recorded disappears.
  const listedEdge = (e: (typeof liveEdges)[number]) =>
    normalizeSequencePair(e) != null || ['aggregation', 'breakdown', 'contains', 'elaboration', 'carries'].includes(e.type);
  const otherLinkRows = linkRows.filter((r) => {
    const e = liveEdges.find((x) => x.id === r.id);
    return e ? !listedEdge(e) : false;
  });
  void groupRow;

  void st.commitmentsAll; // re-render when the registry loads
  const commitmentRows: ViewLinkRow[] = data.commitmentsEnabled
    ? st.getLinkedCommitments(item.id).map((c) => ({
        id: c.id,
        kind: 'Commitment',
        // The commitment's own page, as a collection's name opens its page.
        text: (
          <Link to={`/prioritization/commitments/${c.id}`} className="hover:underline">
            {`${c.title}${c.dueDate ? ` by ${calendarDay(c.dueDate)}` : ''}${c.counterparty ? ` · for ${c.counterparty}` : ''}`}
          </Link>
        ),
        verdict: COVERAGE_LABEL[c.coverage],
        tone: c.coverage === 'covered' ? 'green' : c.coverage === 'at-risk' ? 'coral' : 'mustard',
      }))
    : [];
  // Assignments on this item (959): the record's answer to "who is bound". A rank
  // change never releases one; below every line it reads "Binding, no plan coverage".
  const userNames = Object.fromEntries(st.users.map((u) => [u.id, u.name || u.email]));
  const assignmentRows: ViewLinkRow[] = data.commitmentsEnabled
    ? data.assignments.map((o) => {
        const word = assignmentStandingWord(o);
        const coverage = planCoverageWord(o);
        const who = assigneeLabel(o, { users: userNames });
        const due = o.due_on ? ` by ${calendarDay(o.due_on)}` : o.proposed_due_on ? ` by ${calendarDay(o.proposed_due_on)} (proposed)` : '';
        const level = data.assignmentLevels.get(o.proposed_revision_id ?? '') ?? data.assignmentLevels.get(o.in_force_revision_id ?? '');
        return {
          id: `assignment-${o.assignment_id}`,
          kind: 'Assignment',
          text: `${who} will ${o.verb ?? 'do'} this${due}${o.counterparty ? ` · for ${o.counterparty}` : ''}${level ? ` · ${delegationLevelLabel(level.level, data.delegationLadder)}` : ''}${o.is_binding && coverage && !o.plan_coverage ? ` · ${coverage.toLowerCase()}` : ''}`,
          verdict: word.label,
          tone: word.tone === 'slate' ? 'mustard' : word.tone,
        };
      })
    : [];
  commitmentRows.push(...assignmentRows);

  const userOptions: ChoiceOption[] = [...st.users].sort((x, y) => x.name.localeCompare(y.name)).map((u) => ({ value: u.id, label: u.name || u.email, description: u.name ? u.email : undefined }));
  const people: ViewLinkRow[] = [
    {
      id: 'owner',
      kind: 'Owner',
      text: ownerName,
      anchorId: 'item-owner',
      editor: (close) => (
        <InlineChoiceEditor label="Owner" initial={item.ownerId ?? ''} options={userOptions} allowClear={false} close={close} onSave={(v) => (v ? a.transferOwner(v) : Promise.resolve('Choose a person.'))} />
      ),
    },
    ...(item.delegateId ? [{ id: 'delegate', kind: 'Delegated to', text: nameOf(item.delegateId) ?? 'Someone not listed here' }] : []),
    { id: 'creator', kind: 'Created by', text: nameOf(item.created_by_user_id ?? item.createdBy) ?? (item.createdBy || 'Not recorded') },
  ];

  // ── Prioritization tree ──
  // Each cycle's node, with what sets its chip: the item's rank of record in the plan
  // built from the cycle, and its place on the cycle's most authoritative priority list.
  const cycleNodes = new Map<string, ViewCycleNode & { trackMap: Map<string, ViewTrackNode>; ofRecord?: ItemRanking; list?: ItemRanking }>();
  const loose: LooseEvaluation[] = [];
  const ensureCycle = (id: string, fallbackName?: string | null) => {
    let c = cycleNodes.get(id);
    if (!c) {
      c = { name: cycleName(id, fallbackName), href: `/prioritization/cycles/${id}`, summary: <CycleSummary cycleId={id} />, standing: 'Being considered', standingTone: 'slate', tracks: [], trackMap: new Map() };
      cycleNodes.set(id, c);
    }
    return c;
  };
  const fitText = (t: { requiredItemCategory: string; requiredAltitudeLevel: number }) => {
    if (t.requiredItemCategory === 'UNSCOPED' || t.requiredAltitudeLevel === -1) return 'takes any item';
    const cell = `takes ${categoryLabel(t.requiredItemCategory)} × altitude ${t.requiredAltitudeLevel}`;
    const req = t.requiredItemCategory.toLowerCase();
    if ((category ?? '').toLowerCase() === req && Number(altitude) === t.requiredAltitudeLevel) return `${cell} · fits`;
    const viaFrame = frames.find((f) => frameType(f).toLowerCase() === req && Number(f.altitudeLevel) === t.requiredAltitudeLevel);
    if (viaFrame) return `${cell} · fits through its ${categoryLabel(frameType(viaFrame))} category`;
    return `${cell} · does not fit its cell`;
  };
  // The card that opens from a track's name (TrackSummaryCard): what this page already holds about it.
  const trackSummary = (trackId: string): ReactNode => {
    const t = trackById.get(trackId);
    if (!t) return undefined;
    const pool = poolsWithItem.find((p) => p.trackId === trackId) ?? st.itemPools.find((p) => p.trackId === trackId);
    const cycleId = t.cycleId ?? pool?.cycleId ?? null;
    const ownerId = getTrackOwnerUserIdFromMetadata(t.metadata);
    return (
      <TrackSummaryCard
        itemId={item.id}
        basics={{
          trackId: t.id,
          href: `/prioritization/tracks/${t.id}`,
          ownerName: ownerId ? (nameOf(ownerId) ?? 'Someone not listed here') : null,
          cycleName: cycleId ? cycleName(cycleId) : null,
          pool: pool ? { name: pool.name, itemCount: pool.priorityIds?.length ?? 0, lockedOn: day(pool.lockedAt) } : null,
          criteria: trackCriteria(trackId).map(attrLabel),
          lockedOn: { participation: day(t.participationLockedAt), criteria: day(t.criteriaLockedAt), scales: day(t.rangeSetLockedAt), values: day(t.valuesConvergedAt) },
        }}
      />
    );
  };
  const trackNode = (trackId: string): ViewTrackNode | null => {
    const t = trackById.get(trackId);
    if (!t) return null;
    const pool = poolsWithItem.find((p) => p.trackId === trackId) ?? st.itemPools.find((p) => p.trackId === trackId);
    const criteria = trackCriteria(trackId).map(attrLabel);
    const settings: SettingSource[] = [
      pool
        ? { setting: 'Item pool', value: `${pool.name}${pool.lockedAt ? ` · locked ${day(pool.lockedAt)}` : ''}${poolsWithItem.includes(pool) ? '' : ' · this item is not in it'}`, source: 'set at track', warn: !poolsWithItem.includes(pool) }
        : { setting: 'Item pool', value: 'No pool recorded', source: 'at track', warn: true },
      {
        setting: 'Criteria',
        value: `${criteria.length ? criteria.join(', ') : 'None chosen yet'}${t.criteriaLockedAt ? ` · locked ${day(t.criteriaLockedAt)}` : ''}`,
        source: 'set at track',
        warn: criteria.length === 0,
      },
    ];
    return { name: t.name, href: `/prioritization/tracks/${t.id}`, summary: trackSummary(t.id), fit: fitText(t), settings, sessions: [] };
  };
  const addTrackToCycle = (trackId: string): ViewTrackNode | null => {
    const t = trackById.get(trackId);
    if (!t) return null;
    const pool = poolsWithItem.find((p) => p.trackId === trackId);
    const cycleId = t.cycleId ?? pool?.cycleId ?? null;
    if (!cycleId) return null;
    const c = ensureCycle(cycleId);
    let node = c.trackMap.get(trackId);
    if (!node) {
      node = trackNode(trackId) ?? undefined;
      if (!node) return null;
      c.trackMap.set(trackId, node);
      c.tracks.push(node);
    }
    return node;
  };
  const standaloneSessions = new Map<string, ViewSessionNode[]>();
  for (const tid of trackIdsFromPools) {
    const t = trackById.get(tid);
    if (t && !t.cycleId && !poolsWithItem.find((p) => p.trackId === tid)?.cycleId) standaloneSessions.set(tid, []);
    else addTrackToCycle(tid);
  }
  const sessionNode = (rv: ItemRankedVersion): ViewSessionNode => {
    const lines: string[] = [];
    if (rv.rankedVersion != null) {
      lines.push(
        `Ranked version ${rv.rankedVersion}${rv.rankedTitle && rv.rankedTitle !== item.title ? ` (“${rv.rankedTitle}”)` : ''}.${rv.changedSinceRanked ? ` The item is now version ${rv.currentVersion ?? version ?? '—'}.` : ''}`,
      );
    } else {
      lines.push('Ranked before versions were recorded.');
    }
    if (rv.cycleHasMultipleVersions) lines.push('Sessions in this cycle ranked different versions of the item.');
    const outOf = sessionRanks.find((r) => r.sourceHref?.includes(rv.sessionId))?.outOf;
    const draft = rv.resultStatus && rv.resultStatus !== 'published' && rv.resultStatus !== 'final';
    const href = `/prioritization/sessions/${rv.sessionId}`;
    return {
      name: rv.sessionName,
      href,
      summary: <SessionStepsCard sessionId={rv.sessionId} itemId={item.id} rank={rv.rank} outOf={outOf} href={href} nameOf={nameOf} />,
      date: day(rv.rankedAt),
      verdict: `Result ${rv.rank}${outOf ? ` of ${outOf}` : ''}${draft ? ` · ${rv.resultStatus}` : ''}`,
      tone: draft ? 'slate' : 'neutral',
      lines,
      flagged: rv.cycleHasMultipleVersions || rv.changedSinceRanked,
    };
  };
  for (const rv of data.rankedVersions) {
    const trackId = sessionById.get(rv.sessionId)?.trackId ?? null;
    if (trackId && standaloneSessions.has(trackId)) {
      standaloneSessions.get(trackId)!.push(sessionNode(rv));
      continue;
    }
    const node = trackId ? addTrackToCycle(trackId) : null;
    if (node) {
      node.sessions.push(sessionNode(rv));
    } else {
      const s = sessionNode(rv);
      loose.push({
        title: `${rv.cycleName ? `Cycle · ${rv.cycleName} · ` : 'No track · '}Session · ${rv.sessionName}`,
        refs: [
          ...(rv.cycleName ? [{ kind: 'cycle' as const, name: rv.cycleName, summary: rv.cycleId ? <CycleSummary cycleId={rv.cycleId} /> : undefined }] : []),
          { kind: 'session' as const, name: rv.sessionName, href: s.href, summary: s.summary },
        ],
        standing: s.verdict,
        tone: s.tone,
        text: `${rv.cycleName ? '' : 'Not in a track. '}${s.date}. ${s.lines.join(' ')}`,
      });
    }
  }
  for (const [tid, sess] of standaloneSessions) {
    const t = trackById.get(tid);
    const pool = poolsWithItem.find((p) => p.trackId === tid);
    loose.push({
      title: `No cycle · ${t?.name ?? 'Track'}`,
      refs: [{ kind: 'track', name: t?.name ?? 'Track', href: t ? `/prioritization/tracks/${t.id}` : null, summary: t ? trackSummary(t.id) : undefined }],
      standing: 'Standalone track',
      tone: 'neutral',
      text: `${pool ? `Item pool ${pool.name}. ` : ''}${sess.length ? sess.map((s) => `${s.name}: ${s.verdict}`).join(' · ') : 'No session has ranked it yet.'}`,
    });
  }
  for (const c of cycleNodes.values()) {
    for (const t of c.tracks) if (t.sessions.length === 0) t.emptySession = 'No session has ranked it yet.';
  }
  // A plan of record sits under the prioritization cycle it was built from — the cycle
  // of its item pool or published list, as the database resolves it (migration 843),
  // never guessed from a name. A plan the database ties to no cycle stands on its own.
  for (const r of official) {
    const c = r.cycleId && st.cycles.some((x) => x.id === r.cycleId) ? ensureCycle(r.cycleId) : null;
    if (c) {
      c.ofRecord ??= r;
      c.outcomes = [...(c.outcomes ?? []), { label: 'Plan of record', ref: { kind: 'plan', name: r.sourceName, href: r.sourceHref }, text: `#${r.rank ?? '—'}${r.aboveTheLine === false ? ' · below the line' : ''} · of record since ${day(r.at)}` }];
    } else {
      loose.push({ title: `Plan of record · ${r.sourceName}`, refs: [{ kind: 'plan', name: r.sourceName, href: r.sourceHref }], standing: `#${r.rank ?? '—'}`, tone: 'green', text: `Of record since ${day(r.at, true)}${r.by && nameOf(r.by) ? `, authorized by ${nameOf(r.by)}` : ''}.` });
    }
  }
  for (const r of lists) {
    const trackId = r.sourceHref?.split('/').pop() ?? '';
    const t = trackById.get(trackId);
    const c = t?.cycleId ? ensureCycle(t.cycleId) : null;
    const text = `#${r.rank ?? '—'}${r.outOf ? ` of ${r.outOf}` : ''}${r.aboveTheLine === false ? ' · below the cut' : r.aboveTheLine ? ' above the cut' : ''} · ${rankStandingLabel(r).toLowerCase()}${r.at ? ` ${day(r.at)}` : ''}`;
    if (c) {
      // Lists come most authoritative first, newest first within a standing.
      c.list ??= r;
      c.outcomes = [
        ...(c.outcomes ?? []),
        {
          label: `Priority list · ${r.sourceName}`,
          ref: { kind: 'priority_set', name: r.sourceName, href: r.key.startsWith('set:') ? `/prioritization/reports/priority-set/${r.key.slice(4)}` : r.sourceHref },
          text,
        },
      ];
    } else {
      loose.push({ title: `Priority list · ${r.sourceName}`, refs: [{ kind: 'priority_set', name: r.sourceName, href: r.key.startsWith('set:') ? `/prioritization/reports/priority-set/${r.key.slice(4)}` : r.sourceHref }], standing: rankStandingLabel(r), tone: 'mustard', text });
    }
  }
  for (const c of cycleNodes.values()) {
    for (const [tid] of c.trackMap) {
      const t = trackById.get(tid);
      if (t?.officialSessionId) {
        const s = sessionById.get(t.officialSessionId);
        c.outcomes = [{ label: 'Published track result', text: `${t.name}: ${s?.name ?? 'a session not loaded here'}` }, ...(c.outcomes ?? [])];
      }
    }
  }
  // The chip beside each cycle's name: the rank of record first, else the list's place.
  for (const c of cycleNodes.values()) {
    const chip = cycleStandingChip({ ofRecord: c.ofRecord, list: c.list });
    c.standing = chip.text;
    c.standingTone = chip.tone;
  }
  const cyclesOut: ViewCycleNode[] = [...cycleNodes.values()].map(({ trackMap: _trackMap, ofRecord: _ofRecord, list: _list, ...c }) => c);

  // ── Planning & delivery ──
  const enumOptions = <K extends string>(order: readonly K[], labels: Record<K, string>, descriptions?: Record<K, string>): ChoiceOption[] =>
    order.map((k) => ({ value: k, label: labels[k], description: descriptions?.[k] }));
  const deliveryPlan: ViewLinkRow[] = [
    {
      id: 'placement',
      kind: 'Placement',
      text: official[0] ? `${(officialFirst ?? official[0]).sourceName}${(officialFirst ?? official[0]).rank != null ? ` · #${(officialFirst ?? official[0]).rank}` : ''}` : 'Not in a plan of record yet.',
    },
    {
      id: 'effort',
      kind: 'Effort, capacity units',
      text: item.effortCapacityUnits != null ? `${item.effortCapacityUnits} CU` : null,
      meaning: describeItemEffortSource(effort, item.altitudeLevel),
      anchorId: 'item-effort',
      editor: effortEditor,
    },
  ];
  const deliveryHow: ViewLinkRow[] = [
    // Each type opens under its row to show what it brings (its pattern; for a
    // work type also how that kind of work is approached).
    {
      id: 'work_type',
      kind: 'Work type',
      text: workTypeLabel,
      editor: selectEditor('Work type', item.work_type, workTypeOptions, 'work_type'),
      expand: item.work_type ? <TypePatternDetail listKey="work_type" value={item.work_type} workspaceId={item.workspace_id ?? null} /> : undefined,
    },
    {
      id: 'launch_type',
      kind: 'Launch type',
      text: launchLabel,
      editor: selectEditor('Launch type', item.launch_type, launchOptions, 'launch_type'),
      expand: item.launch_type ? <TypePatternDetail listKey="launch_type" value={item.launch_type} workspaceId={item.workspace_id ?? null} /> : undefined,
    },
    {
      id: 'investment_type',
      kind: 'Investment type',
      text: investmentLabel,
      editor: selectEditor('Investment type', item.investment_type, investmentOptions, 'investment_type'),
      expand: item.investment_type ? <TypePatternDetail listKey="investment_type" value={item.investment_type} workspaceId={item.workspace_id ?? null} /> : undefined,
    },
  ];
  // How sure we are — planning maturity, knowledge status, confidence; not how the work is done (owner, 2026-10-15).
  // With the item_certainty_reading module on, what the record shows comes first: the reading,
  // then one row per gap with the one action that closes it. The typed rows stay and say where
  // they claim more than the record supports.
  const reading = st.certaintyReadingEnabled
    ? deriveItemCertainty({
        item,
        evidence: st.evidence,
        acceptedValues: official_values,
        proposals: st.proposals.filter((p) => p.itemId === item.id),
        criteriaKeys: [...new Set([...criteriaKeys, ...st.attributes.map((x) => x.key)])],
        attributeLabel: attrLabel,
      })
    : null;
  const gapRow = (g: CertaintyLine): ViewLinkRow => {
    const base = { id: `gap-${g.id}`, kind: 'Missing', text: g.text };
    if (g.gap === 'evidence') return { ...base, actions: [{ label: 'Link evidence', onClick: a.addEvidence }] };
    if (g.gap === 'values') return { ...base, actions: [{ label: 'Add a value', onClick: a.goToAssessment }] };
    return { ...base, editor: effortEditor, editLabel: 'Set effort' };
  };
  const readingRows: ViewLinkRow[] = reading
    ? [
        {
          id: 'reading',
          kind: 'What the record shows',
          text: <span data-testid="certainty-reading">{reading.headline}</span>,
          meaning: reading.summary,
          expand: (
            <ul className="list-disc space-y-1 pl-4 text-[13.5px] text-[var(--text-sec)]" data-testid="certainty-basis">
              {reading.lines.map((l) => (
                <li key={l.id}>{l.text}</li>
              ))}
            </ul>
          ),
        },
        ...reading.gaps.map(gapRow),
      ]
    : [];
  const deliveryCertainty: ViewLinkRow[] = [
    ...readingRows,
    {
      id: 'maturity',
      kind: 'Planning maturity',
      text: item.planning_maturity ? PLANNING_MATURITY_LABELS[item.planning_maturity] : null,
      editor: selectEditor('Planning maturity', item.planning_maturity, enumOptions(PLANNING_MATURITY_ORDER, PLANNING_MATURITY_LABELS, PLANNING_MATURITY_DESCRIPTIONS), 'planning_maturity'),
    },
    {
      id: 'knowledge',
      kind: 'Knowledge status',
      text: item.knowledge_status ? KNOWLEDGE_STATUS_LABELS[item.knowledge_status] : null,
      meaning: reading?.typedGaps.knowledge ?? undefined,
      editor: selectEditor('Knowledge status', item.knowledge_status, enumOptions(KNOWLEDGE_STATUS_ORDER, KNOWLEDGE_STATUS_LABELS, KNOWLEDGE_STATUS_DESCRIPTIONS), 'knowledge_status'),
    },
    {
      id: 'confidence',
      kind: 'Confidence',
      // Null is "not assessed", not "low" (migration 937 dropped the low default): a claim nobody made is not drawn as one.
      text: item.confidence_level ? CONFIDENCE_LEVEL_LABELS[item.confidence_level] : <span className="text-[var(--text-dim)]">Not assessed</span>,
      meaning: reading?.typedGaps.confidence ?? undefined,
      editor: selectEditor('Confidence', item.confidence_level, enumOptions(CONFIDENCE_LEVEL_ORDER, CONFIDENCE_LEVEL_LABELS, CONFIDENCE_LEVEL_DESCRIPTIONS), 'confidence_level'),
    },
  ];

  // ── History ──
  const versionRows: ViewLinkRow[] = versions.map((v, i) => {
    const rankedBy = [...new Set(data.rankedVersions.filter((r) => r.rankedVersion === v.version).map((r) => r.sessionName))];
    return {
      id: v.id,
      kind: `v${v.version} · ${day(v.createdAt)}`,
      text: v.changed.length ? `${v.changed.join('. ')}.` : v.version === 1 ? 'First version.' : 'Saved.',
      meaning: rankedBy.length ? `Ranked by ${rankedBy.join(', ')}` : undefined,
      verdict: i === 0 ? 'current' : undefined,
      tone: 'neutral',
    };
  });
  const actorEvents: (ViewLinkRow & { at: string })[] = [];
  const creator = nameOf(item.created_by_user_id ?? item.createdBy);
  const creationSentence = itemCreationSentence(item, creator, item.source ? itemSourceLabel(item.source, st.itemSources) ?? item.source : null);
  actorEvents.push({
    id: 'created',
    at: iso(item.createdAt),
    kind: day(item.createdAt, true),
    text: `Created${item.source ? ` from ${(itemSourceLabel(item.source, st.itemSources) ?? item.source).toLowerCase()}` : ''}`,
    meaning:
      item.creationSource === 'ai'
        ? `drafted by agent${item.creationReviewStatus === 'human_reviewed' ? ' · reviewed by a person' : ' · not reviewed yet'}`
        : item.creationSource === 'automated'
          ? 'automated'
          : creator
            ? `person · ${creator}`
            : undefined,
  });
  for (const p of st.classification?.history ?? EMPTY) {
    if (p.status !== 'accepted' && p.status !== 'rejected') continue;
    const agent = p.proposedBy === 'agent';
    const by = nameOf(p.acceptedByUserId);
    actorEvents.push({
      id: `class:${p.id}`,
      at: iso(p.acceptedAt ?? p.createdAt),
      kind: day(p.acceptedAt ?? p.createdAt, true),
      text: `${categoryLabel(p.classificationFields.category)} · ${altitudeIn(p.classificationFields.altitudeLevel, p.classificationFields.category)}`,
      meaning: agent
        ? `proposed by agent (${p.proposedByAgent ?? 'classifier'}) · ${p.status === 'accepted' ? `accepted${by ? ` by ${by}` : ''}` : 'dismissed'}`
        : `person · ${nameOf(p.proposedByUserId) ?? by ?? 'not recorded'}`,
      verdict: agent ? (p.status === 'accepted' ? 'Accepted suggestion' : 'Dismissed') : undefined,
      tone: agent && p.status === 'accepted' ? 'slate' : 'neutral',
    });
  }
  for (const t of Object.values(st.assessments).filter((x) => x.itemId === item.id)) {
    actorEvents.push({
      id: `triage:${t.id}`,
      at: iso(t.triagedAt),
      kind: day(t.triagedAt, true),
      text: `Triage: ${TRIAGE_LABEL[t.outcome] ?? t.outcome}`,
      meaning: nameOf(t.triagedBy) ? `person · ${nameOf(t.triagedBy)}` : undefined,
    });
  }
  actorEvents.sort((x, y) => y.at.localeCompare(x.at));

  const lineageEvent = (l: (typeof data.lineage)[number]) => {
    const isSource = (l.source_item_ids ?? []).includes(item.id);
    const others = (isSource ? l.target_item_ids : l.source_item_ids) ?? [];
    const words = LINEAGE_WORDS[l.event_type] ?? [l.event_type, l.event_type];
    return { isSource, others, word: isSource ? words[0] : words[1] };
  };
  const lineageRows: ViewLinkRow[] = LINEAGE_ACTS.map((act) => {
    const recorded = data.lineage.filter((l) => l.event_type === act.type);
    return {
      id: act.type,
      kind: act.label,
      text: (
        <>
          {recorded.length === 0 && <span className="block text-[var(--text-dim)]">None</span>}
          {recorded.map((l) => {
            const e = lineageEvent(l);
            return (
              <span key={l.id} className="block text-[var(--text-pri)] [&+&]:mt-1">
                {e.word}{' '}
                {e.others.length
                  ? e.others.map((o, i) => (
                      <span key={o}>
                        {i > 0 ? ', ' : ''}
                        {itemLink(o)}
                      </span>
                    ))
                  : 'no other item recorded'}{' '}
                · {day(l.created_at)}
                {nameOf(l.performed_by) ? ` · ${nameOf(l.performed_by)}` : ''}
              </span>
            );
          })}
          <span className="mt-1 block text-[12.5px] leading-snug text-[var(--text-dim)]">{act.does}</span>
        </>
      ),
      actions: a.lineageAction ? [{ label: act.label, onClick: () => a.lineageAction?.(act.type) }] : undefined,
    };
  });

  const lineageActTypes = new Set<string>(LINEAGE_ACTS.map((x) => x.type));
  const identityRows: ViewLinkRow[] = data.lineage.filter((l) => !lineageActTypes.has(l.event_type)).map((l) => {
    const { others, word } = lineageEvent(l);
    return {
      id: l.id,
      kind: `${word} · ${day(l.created_at)}`,
      text: others.length ? (
        <>
          {others.map((o, i) => (
            <span key={o}>
              {i > 0 ? ', ' : ''}
              {itemLink(o)}
            </span>
          ))}
        </>
      ) : (
        'No other item recorded.'
      ),
      meaning: [l.rationale ? `“${l.rationale}”` : '', nameOf(l.performed_by) ? `· ${nameOf(l.performed_by)}` : ''].filter(Boolean).join(' ') || undefined,
    };
  });
  if (item.sourceSessionId) {
    identityRows.push({
      id: 'promoted',
      kind: `Promoted from a session${item.promotedAt ? ` · ${day(item.promotedAt)}` : ''}`,
      text: (
        <Link to={`/prioritization/sessions/${item.sourceSessionId}`} className="hover:underline">
          {sessionById.get(item.sourceSessionId)?.name ?? 'A session not loaded here'}
        </Link>
      ),
      meaning: nameOf(item.promotedBy) ? `· ${nameOf(item.promotedBy)}` : undefined,
    });
  }

  // ── Title editing (migration 913 lock) ──
  const locked = !!data.lock?.locked;
  const canEdit = !locked || !!data.lock?.canChange;
  const onCurrent = data.rankedVersions.filter((r) => version != null && r.rankedVersion === version);
  const onCurrentCycles = [...new Set(onCurrent.map((r) => r.cycleName).filter((n): n is string => !!n))];

  // ── Rail ──
  const kindText = category ? `${categoryLabel(category)}${frames.length ? ` · +${frames.length}` : ''}` : 'Not classified';
  // Idea source: picked from the workspace list; set by itself on arrival (migration 926).
  const ideaSourceText = itemSourceLabel(item.source, st.itemSources);
  const activeSources = st.itemSources.filter((x) => x.isActive);
  const sourceOptions: ChoiceOption[] = [
    ...activeSources.filter((x) => x.kind === 'idea').map((x) => ({ value: x.value, label: x.label })),
    ...activeSources
      .filter((x) => x.kind === 'arrival')
      .map((x) => ({ value: x.value, label: x.label, description: 'Set on arrival' })),
    ...(item.source && !activeSources.some((x) => x.value === item.source)
      ? [{ value: item.source, label: ideaSourceText ?? item.source, description: 'Not in the workspace list' }]
      : []),
  ];
  const sourceText = [
    item.creationSource === 'ai' ? `drafted by agent${item.creationReviewStatus === 'human_reviewed' ? ', reviewed' : ''}` : item.creationSource === 'automated' ? 'automated' : item.creationSource === 'human' ? 'by a person' : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const queueOptions: ChoiceOption[] = [...st.orgUnits.values()]
    .filter((u) => u.queueSettings?.enabled)
    .map((u) => ({ value: u.id, label: u.displayName || u.name }))
    .sort((x, y) => x.label.localeCompare(y.label));
  const queueLabel = item.queueId
    ? resolveQueueLabel(item.queueId, st.orgUnits, queueOptions.map((q) => ({ id: q.value, name: q.label }))) ?? 'A queue not loaded here'
    : 'Not in a queue';
  const evalCycles = cycleNodes.size;
  const evalTracks = [...cycleNodes.values()].reduce((n, c) => n + c.tracks.length, 0) + standaloneSessions.size;
  const evalSessions = new Set(data.rankedVersions.map((r) => r.sessionId)).size;
  const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

  // Each count in the right column opens the things it counts, each marked with its kind and linked to it.
  type Ref = { kind: ObjectKind; name: string; href?: string | null; summary?: ReactNode };
  const refList =
    (refs: Ref[]) =>
    (close: () => void): ReactNode => (
      <ul className="space-y-1.5" onKeyDown={(e) => e.key === 'Escape' && close()}>
        {refs.map((r, i) => (
          <li key={`${r.kind}:${r.href ?? r.name}:${i}`}>
            <ObjectRef kind={r.kind} name={r.name} href={r.href} summary={r.summary} />
          </li>
        ))}
      </ul>
    );
  const opens = (refs: Ref[]) => (refs.length ? refList(refs) : undefined);
  const cycleRefs: Ref[] = [...cycleNodes.values()].map((c) => ({ kind: 'cycle', name: c.name, href: c.href, summary: c.summary }));
  const trackRefs: Ref[] = [
    ...[...cycleNodes.values()].flatMap((c) => c.tracks.map((t) => ({ kind: 'track' as const, name: t.name, href: t.href, summary: t.summary }))),
    ...[...standaloneSessions.keys()].map((tid) => ({ kind: 'track' as const, name: trackById.get(tid)?.name ?? 'Track', href: `/prioritization/tracks/${tid}`, summary: trackSummary(tid) })),
  ];
  const sessionRefs: Ref[] = [...new Map(data.rankedVersions.map((r) => [r.sessionId, r.sessionName])).entries()].map(([id, name]) => ({
    kind: 'session',
    name,
    href: `/prioritization/sessions/${id}`,
  }));
  const myCollectionIdsForRail = new Set(st.collectionMemberships.filter((cm) => cm.itemId === item.id).map((cm) => cm.collectionId));
  const collectionRefs: Ref[] = st.collections
    .filter((c) => myCollectionIdsForRail.has(c.id))
    .map((c) => ({ kind: 'collection', name: c.name || 'Unnamed collection', href: `/prioritization/collections/${c.id}` }));
  const poolRefs: Ref[] = poolsWithItem.map((p) => ({ kind: 'item_pool', name: p.name, href: `/prioritization/lists/${p.id}` }));
  const resultRefs: Ref[] = sessionRanks.map((r) => ({ kind: 'session_result', name: `${r.sourceName}${r.rank != null ? ` · #${r.rank}` : ''}`, href: r.sourceHref }));
  const setRefs: Ref[] = lists.map((r) => ({
    kind: 'priority_set',
    name: `${r.sourceName}${r.rank != null ? ` · #${r.rank}` : ''}`,
    href: r.key.startsWith('set:') ? `/prioritization/reports/priority-set/${r.key.slice(4)}` : r.sourceHref,
  }));
  const planRefs: Ref[] = official.map((r) => ({ kind: 'plan', name: `${r.sourceName}${r.rank != null ? ` · #${r.rank}` : ''}`, href: r.sourceHref }));

  // ── The standard (lib/itemStandard.ts): what the page already holds, read in one place ──
  const customLabels = new Set(st.attributes.filter((x) => x.isCustomField).map((x) => x.label));
  const promises = data.commitmentsEnabled
    ? [
        ...st.getLinkedCommitments(item.id).map((c) => ({ title: c.title, due: c.dueDate ? calendarDay(c.dueDate) : null })),
        ...data.assignments.map((o) => ({ title: `${assigneeLabel(o, { users: userNames })} will ${o.verb ?? 'do'} this`, due: o.due_on ? calendarDay(o.due_on) : null })),
      ]
    : [];
  const standardReading = readItemStandard(item, {
    classification: category ? `${categoryLabel(category)}${altitude ? ` at altitude ${altitudeIn(altitude, category, true)}` : ''}` : null,
    values: attributeRows.filter((r) => r.accepted && !customLabels.has(r.attribute)).map((r) => ({ label: r.attribute, value: r.accepted as string })),
    criteriaKeys: [...criteriaKeys],
    attributeLabel: attrLabel,
    promises,
    plan: officialFirst ? { name: officialFirst.sourceName, since: officialFirst.at ? day(officialFirst.at, true) : null } : null,
    reviewOn: item.exit_status === 'deferred' && item.deferReviewDate ? day(item.deferReviewDate, true) : null,
    supports: (data.strategy?.groups ?? []).flatMap((g) => g.objectives.map((o) => o.objective.name)),
    parent: item.parentItemId ? titleOf(item.parentItemId) : null,
    product: st.productName,
    evaluations: [...cyclesOut.map((c) => c.name), ...loose.map((l) => l.title)],
    relatedCount: new Set(liveEdges.map((e) => (e.sourceItemId === item.id ? e.targetItemId : e.sourceItemId))).size,
    versionCount: data.versions === null ? null : data.versions.length,
    origin: creationSentence,
    originKnown: !!creator || !!item.source || item.creationSource === 'ai' || item.creationSource === 'automated',
    eventCount: actorEvents.length + data.lineage.length,
  });
  // The title and description are changed in the header, unless they are locked or kept in another system.
  // In Action the plan holds them too (1060): only someone who may amend it changes them here.
  const mayChangeWords = canEdit && (!planLock || planLock.mayAmend);
  const mayFillWords = a.mayEdit && mayChangeWords && !(keptElsewhere(st.fieldSources?.byField.title) && keptElsewhere(st.fieldSources?.byField.description));
  const fills: Record<ItemStandardFill, { label: string; to: PageTarget; may: boolean }> = {
    description: { label: 'Add a description', to: { tab: 'assessment', edit: 'title' }, may: mayFillWords },
    category: { label: 'Set category and altitude', to: { tab: 'overview', edit: 'what:categories', anchor: 'item-category' }, may: a.mayEdit },
    why: { label: 'Say why, and why now', to: { tab: 'overview', edit: 'what:why', anchor: 'item-why' }, may: a.mayEdit },
    values: { label: 'Go to the values', to: { tab: 'assessment', anchor: 'item-attributes' }, may: true },
    context: data.strategy
      ? { label: 'Link it to an objective', to: { tab: 'connections', anchor: 'item-strategy' }, may: a.mayEdit }
      : { label: 'Link it to other items', to: { tab: 'connections', anchor: 'item-relationships' }, may: a.mayEdit },
    owner: { label: 'Name an owner', to: { tab: 'connections', edit: 'people:owner', anchor: 'item-owner' }, may: a.mayEdit },
    history: { label: 'See its history', to: { tab: 'history' }, may: true },
  };
  const standard: ItemStandardView = {
    sufficient: standardReading.sufficient,
    line: SUFFICIENT.words,
    answers: standardReading.answers.map((answer) => ({
      id: answer.id,
      question: answer.question,
      holds: answer.holds,
      held: answer.held,
      missing: answer.missing,
      links: answer.fills.map((f) => fills[f]).filter((f) => f.may).map(({ label, to }) => ({ label, to })),
    })),
  };

  return {
    backHref: '/prioritization/items',
    badge: category && altitude ? { category: categoryLabel(category), altitude: Number(altitude), tag: altitudeTagOf(altitude, category, altitudeLabelsOf(category)) } : null,
    state: { label: exit.label, tone: exit.tone },
    owner: ownerName,
    version,
    askAi: a.askAi,
    discuss: {
      count: st.comments.filter((c) => c.entityType === 'item' && c.entityId === item.id).length,
      onClick: a.openDiscussion,
    },
    watch: { watching: st.watching, onToggle: a.toggleWatch },
    more: [
      { label: 'Print view', onClick: a.openPrint },
      { label: 'Integrations', onClick: a.openIntegrations },
      { label: 'Who can see it', onClick: a.openVisibility },
      { label: 'Copy link', onClick: a.copyLink },
      ...(a.lineageAction ? [{ label: 'Split, merge or fork', onClick: () => a.lineageAction?.('split') }] : []),
      ...(a.takeBack ? [{ label: 'Take back', onClick: a.takeBack }] : []),
      ...(a.inspect ? [{ label: 'Inspect assembled item (admin)', onClick: a.inspect }] : []),
      ...(a.retire ? [{ label: 'Retire', onClick: a.retire, danger: true }] : []),
    ],
    title: item.title,
    description: item.description ?? '',
    fieldSources: { title: st.fieldSources?.byField.title, description: st.fieldSources?.byField.description },
    titleEdit: {
      version,
      canEdit: mayChangeWords,
      cannotEditReason: !canEdit ? (
        'A ranking has used what this item says, so it is locked. A workspace owner, an admin or someone with decision authority can change it; their change makes a new version and earlier rankings keep theirs.'
      ) : planLock && !planLock.mayAmend ? (
        <>
          {planLock.name} holds what this item says as it was authorized. The plan&rsquo;s owner, its executive sponsor or a workspace admin changes it by amending the plan
          {planLock.rankingCycles.length > 0 && (
            <>
              ; it can also be changed for{' '}
              {planLock.rankingCycles.map((c, i) => (
                <span key={c.href}>
                  {i > 0 ? ', ' : ''}
                  <Link to={c.href} className="underline">
                    {c.name}
                  </Link>
                </span>
              ))}
              , still ranking it, and the plan keeps that change out
            </>
          )}
          .
        </>
      ) : undefined,
      current: a.currentWords ?? undefined,
      askWhy: false,
      notice: (v) => {
        if (planLock) return `This amends ${planLock.name}, which keeps version ${v ?? 'it holds'} until it is authorized again.`;
        if (!locked) return 'Nothing has ranked this item yet, so no group keeps an earlier version.';
        if (onCurrent.length > 0 && v != null) {
          const n = new Set(onCurrent.map((r) => r.sessionId)).size;
          return `${n} ${n === 1 ? 'group' : 'groups'} ranked version ${v}${onCurrentCycles.length ? ` in ${onCurrentCycles.join(', ')}` : ''}, and they keep seeing version ${v} with their results. A session that starts after this sees version ${v + 1}.`;
        }
        return `Earlier rankings keep the version they ranked. A session that starts after this sees the new version.`;
      },
      onSave: async (title, description) => {
        if (!planLock) return save({ title, description });
        setAmendingPlan(planLock.planCycleId);
        try {
          return await save({ title, description });
        } finally {
          setAmendingPlan(null);
        }
      },
    },
    standing,
    attention,
    attentionNote: 'Required and Missing come from the every-item rules in Settings → Readiness. Readiness to rank depends on the evaluation, so each line names where it applies.',
    whatItIs,
    proposal,
    standard,
    assessment: {
      attributeOptions: attrKeys.filter((k) => st.attributes.some((x) => x.key === k)).map((k) => ({ key: k, label: attrLabel(k) })),
      intro: (
        <>
          Showing the workspace’s <strong className="text-[var(--text-pri)]">accepted values</strong> for this item. An attribute becomes a criterion when an evaluation chooses
          it. Select a row to set its value on each of its scales, add an estimate, or see who set it.
        </>
      ),
      rows: assessmentRows,
      initialOpen: null,
      detailActions: (row): ActionLink[] => (row.attribute === 'Item effort' || row.attribute === 'Work type' || row.attribute === 'Launch type' ? [] : [{ label: 'Link evidence', onClick: a.addEvidence }]),
    },
    evidence: { rows: evidenceRows, add: { onClick: a.addEvidence }, empty: 'No evidence attached yet.' },
    strategy: data.strategy ? buildStrategyRows(data.strategy, a) : null,
    relationships: { rows: relationshipRows },
    links: {
      rows: otherLinkRows,
      add: null,
      find: a.findLinks ? ({ onClick: a.findLinks } as ViewButton) : null,
      empty: '',
    },
    commitments: data.commitmentsEnabled ? { rows: commitmentRows, empty: 'No commitment names this item.' } : null,
    people,
    prioritization: {
      intro: 'Cycle → track → session. Each track shows its item pool and criteria and where they are set. Change them on that track or session; it affects everything in its pool.',
      cycles: cyclesOut,
      loose,
      empty: 'Not in any cycle, track or session yet.',
    },
    delivery: [
      { title: 'In the plan and how it turned out', owned: 'same units on both sides', rows: deliveryPlan },
      { title: 'How it will be done', owned: 'owned by this item · edit here', rows: deliveryHow },
      { title: 'How sure we are', owned: 'about the estimate · edit here', rows: deliveryCertainty },
    ],
    versions: { rows: versionRows, empty: data.versions === null ? 'Versions could not be read.' : 'No versions recorded yet.' },
    actors: { rows: actorEvents, empty: 'Nothing recorded yet.' },
    creation: creationSentence,
    identity: { rows: identityRows, journey: { onClick: a.openJourney }, empty: 'Not promoted from a session, and never retired.' },
    lineage: { rows: lineageRows },
    rail: [
      {
        h: 'At a glance',
        rows: [
          // Category and altitude edit in place, where they are clicked (owner, 2026-10-15).
          { k: 'Category', v: kindText, editor: inPlan('Category', (close) => <CategoriesEditor item={item} close={close} save={save} />) },
          {
            k: 'Altitude',
            v: altitudeText(altitude, category, altitudeLabelsOf(category)),
            editor: inPlan('Altitude', (close) => (
              <InlineChoiceEditor
                stacked
                label="Altitude"
                initial={altitude ? String(altitude) : ''}
                options={([5, 4, 3, 2, 1] as const).map((n) => ({ value: String(n), label: altitudeIn(n, category, true) ?? String(n) }))}
                close={close}
                onSave={(v) => save({ altitudeLevel: v ? (Number(v) as Item['altitudeLevel']) : undefined })}
              />
            )),
          },
          {
            k: 'Owner',
            v: ownerName ?? 'Not set',
            editor: (close) => (
              <InlineChoiceEditor stacked label="Owner" initial={item.ownerId ?? ''} options={userOptions} allowClear={false} close={close} onSave={(v) => (v ? a.transferOwner(v) : Promise.resolve('Choose a person.'))} />
            ),
          },
          {
            k: 'State',
            v: exit.word,
            editor: (close) => (
              <InlineChoiceEditor
                stacked
                label="State"
                initial={item.exit_status ?? 'active'}
                allowClear={false}
                options={[
                  { value: 'active', label: 'Active', description: 'In the lists and open to evaluation.' },
                  { value: 'deferred', label: 'Deferred', description: 'Set aside for now. Usually hidden from default lists.' },
                  { value: 'archived', label: 'Archived', description: 'Out of active lists. Its history stays.' },
                ]}
                close={close}
                onSave={(v) => save({ exit_status: v as Item['exit_status'] })}
              />
            ),
          },
          {
            k: 'Idea source',
            v: ideaSourceText ?? 'Not set',
            editor:
              canEdit && sourceOptions.length
                ? (close: () => void) => (
                    <InlineChoiceEditor stacked label="Idea source" initial={item.source ?? ''} options={sourceOptions} close={close} onSave={(v) => save({ source: v || null } as Partial<Item>)} />
                  )
                : undefined,
          },
          { k: 'Created', v: sourceText || 'Not recorded', to: { tab: 'history' } },
          { k: 'Version', v: version != null ? String(version) : '—', to: { tab: 'history' } },
          // Custom fields placed "At a glance" (Settings → Custom Fields → Shows on).
          ...fieldsInSpot(st.attributes, 'glance').map((field) => ({
            k: field.label,
            v: formatCustomFieldValue(field, (item.metadata as Record<string, unknown> | undefined)?.[field.key]) ?? 'Not set',
            editor: canEdit
              ? (close: () => void) => (
                  <div className="space-y-2">
                    <CustomFieldInput field={field} metadata={item.metadata as Record<string, unknown> | undefined} onSave={(u) => void save(u as Partial<Item>)} />
                    <button type="button" className="text-xs underline text-[var(--text-ter)]" onClick={close}>Done</button>
                  </div>
                )
              : undefined,
          })),
        ],
      },
      {
        h: 'Where it sits',
        rows: [
          {
            k: 'Linked in',
            v:
              st.externalLinks === null
                ? '…'
                : st.externalLinks.length || item.external_id
                  ? [
                      ...st.externalLinks.map((l) => `${getConnectorDefinition(l.connectorType as ConnectorType)?.name ?? l.connectorType} ${externalLinkDisplayKey(l)}`),
                      ...(item.external_id && !st.externalLinks.some((l) => l.externalId === item.external_id) ? [item.external_id] : []),
                    ].join(' · ')
                  : 'Not linked',
            to: { tab: 'connections', anchor: 'item-linked-systems' },
          },
          { k: 'Kept in', v: keptInText(st.fieldSources, (k) => st.attributes.find((x) => x.key === k)?.label ?? k), to: { tab: 'connections', anchor: 'item-linked-systems' } },
          {
            k: 'Queue',
            v: queueLabel,
            editor: queueOptions.length
              ? (close) => (
                  <InlineChoiceEditor stacked label="Queue" initial={item.queueId ?? ''} options={queueOptions} close={close} onSave={(v) => save({ queueId: v || null } as Partial<Item>)} />
                )
              : undefined,
          },
          ...(st.productHierarchyEnabled || item.productNodeId
            ? [
                {
                  k: 'Product',
                  v: item.productNodeId ? st.productName ?? 'A product not loaded here' : 'Not placed',
                  editor: st.productOptions.length
                    ? (close: () => void) => (
                        <InlineChoiceEditor stacked label="Product" initial={item.productNodeId ?? ''} options={st.productOptions} close={close} onSave={(v) => save({ productNodeId: v || null } as Partial<Item>)} />
                      )
                    : undefined,
                },
              ]
            : []),
          {
            k: 'Evaluations',
            v: `${plural(evalCycles, 'cycle')} · ${plural(evalTracks, 'track')} · ${plural(evalSessions, 'session')}`,
            editor: opens([...cycleRefs, ...trackRefs, ...sessionRefs]),
          },
        ],
      },
      {
        h: 'Lists it’s in',
        rows: [
          { k: 'Collections', v: String(collectionRefs.length), editor: opens(collectionRefs) },
          { k: 'Item pools', v: String(poolRefs.length), editor: opens(poolRefs) },
          { k: 'Results', v: plural(resultRefs.length, 'session'), editor: opens(resultRefs) },
          { k: 'Priority lists', v: bestList ? `${lists.length} · #${bestList.rank ?? '—'}` : String(lists.length), editor: opens(setRefs) },
          { k: 'Plans', v: String(planRefs.length), editor: opens(planRefs) },
        ],
      },
    ],
  };
}

/** One line for the rail: which fields cross to which systems; everything else is kept here. */
function keptInText(sources: ItemFieldSources | null, attributeLabel: (key: string) => string = (k) => k): string {
  if (sources === null) return '…';
  const crossing = (Object.keys(sources.byField) as SyncedItemField[]).filter((f) => sources.byField[f]?.length);
  const crossingAttributes = Object.keys(sources.byAttribute ?? {}).filter((k) => sources.byAttribute[k]?.length);
  if (!crossing.length && !crossingAttributes.length) return 'Priorities.ai — every field';
  const parts = [
    ...crossing.map((f) => `${SYNCED_FIELD_LABELS[f]} ${sources.byField[f]!.map(sourceWords).join(', ')}`),
    ...crossingAttributes.map((k) => `${attributeLabel(k)} ${sources.byAttribute[k].map(sourceWords).join(', ')}`),
  ];
  return `${parts.join(' · ')} · everything else Priorities.ai`;
}
